import React, { useEffect, useRef, useState } from "react";
import api, { FRONTEND_BUILD_ID } from "../lib/api";
import GrowthAgentSection from "./GrowthAgentTab";
import { EmailHealthSection } from "./EmailHealthTab";
import FounderTeamsSection from "./FounderTeamsTab";
import FounderPartnersSection from "./FounderPartnersTab";
import { FounderUnifiedPartnersSection } from "./FounderUnifiedPartnersTab";
import { FounderCreatePartnerLinkSection } from "./FounderCreatePartnerLinkTab";
import { FounderOpsSection } from "./FounderOpsTab";

/**
 * Web Control Room — full 7-section founder dashboard.
 * Web-only metrics. Smoke + test-payment filtered server-side.
 * WhatsApp-era panels live at /admin/legacy.
 */
const WINDOW_OPTIONS = [7, 30, 90, 365];

export default function WebControlRoom() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  // P0 2026-10-01: manual-retry trigger for the error state below.
  const [retryN, setRetryN] = useState(0);
  // STAGE 1 frontend perf (2026-10-06): manual-refresh trigger + last-updated
  // timestamp. The old 60s auto-refresh is gone (see the effect below).
  const [refreshN, setRefreshN] = useState(0);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  // since_days controls the Activation Funnel, Rejection breakdown, and
  // Review-time median/p95 sections. Default 90 matches the backend default.
  // Persisted to localStorage so the founder doesn't have to re-toggle on
  // every reload.
  const [sinceDays, setSinceDays] = useState(() => {
    try {
      const v = parseInt(localStorage.getItem("gully_wcr_since_days") || "90", 10);
      return WINDOW_OPTIONS.includes(v) ? v : 90;
    } catch { return 90; }
  });
  // Commercial North Star window — default 3d, optional 7d.
  const [nsfWindow, setNsfWindow] = useState(() => {
    try {
      const v = localStorage.getItem("gully_wcr_nsf_window") || "3d";
      return v === "7d" ? "7d" : "3d";
    } catch { return "3d"; }
  });

  // STAGE 1 frontend perf (2026-10-06): the old 60s setInterval auto-refresh is
  // replaced with a manual Refresh button + a 5-minute background refresh.
  // The mega-endpoint takes ~20s today — the founder's browser no longer
  // re-waits on it every minute. The P0 2026-10-01 30s hard timeout is kept.
  useEffect(() => {
    let alive = true;
    async function load() {
      try {
        const { data } = await api.get("/admin/web/founder-dashboard", {
          params: { since_days: sinceDays, nsf_window: nsfWindow },
          // P0 2026-10-01: hard timeout so a slow/hung admin request fails
          // visibly instead of sitting on "Loading…" forever.
          timeout: 30000,
        });
        if (alive) { setData(data); setErr(null); setLastUpdatedAt(new Date()); }
      } catch (e) {
        if (alive) setErr(e?.code === "ECONNABORTED"
          ? "Dashboard request timed out after 30 seconds — the server took too long. Please retry."
          : (e?.response?.data?.detail || "Could not load."));
      } finally { if (alive) setLoading(false); }
    }
    load();
    const t = setInterval(() => { setRefreshN((n) => n + 1); }, 300000);
    return () => { alive = false; clearInterval(t); };
  }, [sinceDays, nsfWindow, retryN, refreshN]);

  function manualRefresh() {
    setErr(null);
    setRefreshN((n) => n + 1);
  }

  // STAGE 2 founder view (2026-10-06): shared per-platform funnel fetch for
  // MONEY + ANOMALIES (one 4-request parallel batch, not per-section).
  const funnels = useMoneyFunnels(refreshN);
  const anomalies = computeAnomalies(data, funnels);

  function changeWindow(v) {
    setSinceDays(v);
    try { localStorage.setItem("gully_wcr_since_days", String(v)); } catch { /* ignore */ }
  }

  function changeNsfWindow(v) {
    setNsfWindow(v);
    try { localStorage.setItem("gully_wcr_nsf_window", v); } catch { /* ignore */ }
  }

  if (loading && !data) return <Surface><div className="p-6 text-neutral-500" data-testid="wcr-loading">Loading…</div></Surface>;
  if (err) return <Surface><div className="p-6" data-testid="wcr-error">
    <div className="text-red-400">Error: {err}</div>
    <button
      type="button"
      onClick={() => { setErr(null); setLoading(true); setRetryN((n) => n + 1); }}
      className="mt-3 px-3 py-2 rounded-lg border border-neutral-700 text-sm text-neutral-200 hover:bg-neutral-800"
    >Retry</button>
  </div></Surface>;
  if (!data) return null;

  return (
    <div className="space-y-12" data-testid="web-control-room">
      <StaleFrontendBanner />
      <RefreshControls onRefresh={manualRefresh} lastUpdatedAt={lastUpdatedAt} />
      <WindowToggle current={sinceDays} label={data.window_label} onChange={changeWindow} />
      {/* STAGE 2 founder view (2026-10-06): four primary landing sections, in
          order — TODAY / PLAYER CHAIN / MONEY / ANOMALIES. Everything else is
          de-emphasized into collapsed areas below; no section or endpoint is
          destroyed. Presentation-only: no metric definition changes. */}
      <SectionToday data={data} anomalyCount={anomalies.length} />
      <SectionPlayerChain refreshKey={refreshN} />
      <SectionMoney data={data} funnels={funnels} />
      <SectionAnomalies items={anomalies} />
      <details className="rounded-2xl border border-neutral-900 bg-neutral-950/40 px-4 py-5" data-testid="wcr-more-detail">
        <summary className="cursor-pointer label-eyebrow hover:text-neutral-300">
          more detail · de-emphasized tiles
        </summary>
        <div className="mt-6 space-y-12">
          <SectionCommercialNorthStar
            nsf={data.commercial_north_star}
            currentWindow={nsfWindow}
            onChangeWindow={changeNsfWindow}
          />
          <SectionCueLifecycle counts={data.cue_lifecycle} windowLabel={data.window_label} />
          <SectionWeeklyReflections wr={data.weekly_reflections} windowLabel={data.window_label} />
          <SectionContinuityObservation obs={data.continuity_observation} wr={data.weekly_reflections} videoHealth={data.video_health} windowLabel={data.window_label} />
          {/* STAGE 2: the 50k intelligence keyword scan is collapsed into a
              "what they're bringing" expander. The scan itself runs server-side
              as part of the mega-endpoint (computed regardless); a backend
              lane would need to make it optional to truly skip it on landing. */}
          <details data-testid="wcr-intelligence-expander">
            <summary className="cursor-pointer label-eyebrow hover:text-neutral-300">
              what they&apos;re bringing · keyword themes
            </summary>
            <div className="mt-4">
              <Section6Intelligence intel={data.intelligence} />
            </div>
          </details>
        </div>
      </details>
      <details className="rounded-2xl border border-neutral-900 bg-neutral-950/40 px-4 py-5" data-testid="wcr-all-dashboards">
        <summary className="cursor-pointer label-eyebrow hover:text-neutral-300">
          all dashboards · everything else
        </summary>
        <div className="mt-6 space-y-12">
          {/* STAGE 1 frontend perf (2026-10-06): the ~20 self-fetching sections
              below are lazy-loaded on scroll — they mount (and fire their API
              requests) only when scrolled into view. Landing fires just the
              mega-endpoint + player-chain + 4 small funnel reads instead of ~25.
              Order and content of every section are unchanged; nothing is removed. */}
          <LazySection name="teams" title="teams"><FounderTeamsSection /></LazySection>
          <LazySection name="ops" title="ops"><FounderOpsSection /></LazySection>
          <LazySection name="partners-unified" title="partners"><FounderUnifiedPartnersSection /></LazySection>
          <LazySection name="partner-link" title="create partner link"><FounderCreatePartnerLinkSection /></LazySection>
          <LazySection name="partners" title="partners"><FounderPartnersSection /></LazySection>
          <LazySection name="email-health" title="email health"><EmailHealthSection /></LazySection>
          <LazySection name="growth-agent" title="growth agent"><GrowthAgentSection /></LazySection>
          <Section1 data={data} />
          <LazySection name="retention" title="retention investigation"><SectionRetentionInvestigation /></LazySection>
          <Section2Funnel funnel={data.activation_funnel} windowLabel={data.window_label} />
          <LazySection name="acquisition-funnel" title="acquisition funnel"><SectionAcquisitionFunnel /></LazySection>
          <Section3Video health={data.video_health} windowLabel={data.window_label} />
          <LazySection name="video-health" title="video health scoreboard"><SectionVideoHealthScoreboard /></LazySection>
          <LazySection name="video-four-category" title="video 4-category"><SectionVideoFourCategory sinceDays={sinceDays} /></LazySection>
          <SectionClientRefSearch />
          <LazySection name="upload-failures" title="upload failures"><SectionUploadFailures /></LazySection>
          <LazySection name="review-disputes" title="review disputes"><SectionReviewDisputes /></LazySection>
          <Section4Revenue rev={data.revenue} />
          <LazySection name="plus-analytics" title="plus launch analytics"><SectionPlusLaunchAnalytics sinceDays={sinceDays} /></LazySection>
          <LazySection name="conversion-intelligence" title="conversion intelligence"><SectionConversionIntelligence /></LazySection>
          <LazySection name="passes" title="gully passes"><SectionGullyPasses /></LazySection>
          <LazySection name="abandoned-plus" title="abandoned plus"><SectionAbandonedPlus /></LazySection>
          <LazySection name="cricket-intelligence" title="cricket intelligence"><SectionCricketIntelligence /></LazySection>
          <LazySection name="conversations" title="conversations"><Section5Conversations /></LazySection>
          <LazySection name="power-users" title="power users"><Section7PowerUsers /></LazySection>
        </div>
      </details>
      <details className="text-xs text-neutral-500" data-testid="wcr-notes">
        <summary className="cursor-pointer hover:text-neutral-300">Data quality notes</summary>
        <ul className="mt-2 space-y-1 pl-4 list-disc">
          {data.data_quality_notes.map((n, i) => <li key={i}>{n}</li>)}
        </ul>
      </details>
    </div>
  );
}

// ============================================================================
// Window toggle — applies to Activation Funnel + Rejection + Review-time
// median/p95 sections only. Section 1 (24h/7d) and Section 4 (24h revenue)
// remain fixed-window because those windows are part of the founder's
// mental model.
function WindowToggle({ current, label, onChange }) {
  return (
    <div className="flex items-center justify-between gap-3 flex-wrap" data-testid="wcr-window-toggle">
      <div>
        <p className="label-eyebrow">window</p>
        <p className="text-xs text-neutral-500 mt-0.5">
          activation funnel · rejection breakdown · review-time stats — {label || "—"}
        </p>
      </div>
      <div className="flex gap-1 text-xs">
        {WINDOW_OPTIONS.map((d) => (
          <button
            key={d}
            onClick={() => onChange(d)}
            className={`px-3 py-1.5 rounded border ${
              d === current
                ? "bg-neutral-800 border-neutral-700 text-neutral-100"
                : "bg-neutral-950 border-neutral-900 text-neutral-500 hover:text-neutral-300"
            }`}
            data-testid={`wcr-window-${d}`}
          >
            {d === 365 ? "1y" : `${d}d`}
          </button>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// STAGE 1 frontend perf (2026-10-06) — manual refresh + 5-minute background
// refresh. Replaces the old 60s auto-refresh on the mega-endpoint.
function RefreshControls({ onRefresh, lastUpdatedAt }) {
  return (
    <div className="flex items-center justify-between gap-3 flex-wrap" data-testid="wcr-refresh-controls">
      <button
        type="button"
        onClick={onRefresh}
        className="px-4 py-2 rounded-lg border border-neutral-700 bg-neutral-800 text-sm text-neutral-100 hover:bg-neutral-700"
        data-testid="wcr-refresh"
      >
        ⟳ Refresh dashboard
      </button>
      <p className="text-xs text-neutral-500" data-testid="wcr-last-updated">
        {lastUpdatedAt ? `last updated ${lastUpdatedAt.toLocaleTimeString()}` : "loading…"}
        {" · "}auto-refreshes every 5 min
      </p>
    </div>
  );
}

// ============================================================================
// STAGE 1 frontend perf (2026-10-06) — lazy-load the ~20 fan-out sections.
// A wrapped section mounts (and fires its API requests) only when scrolled
// into view, or when the founder clicks its placeholder. Landing fires just
// the mega-endpoint + auth/session instead of ~25 requests. Every section's
// underlying endpoint and functionality are untouched — they work exactly as
// before once mounted.
function LazySection({ name, title, children }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const show = () => setVisible(true);
  useEffect(() => {
    if (visible) return;
    const el = ref.current;
    if (!el) return;
    // No IntersectionObserver (jsdom / old browsers) → mount immediately so
    // tests and non-visual renders keep working.
    if (typeof IntersectionObserver === "undefined") { setVisible(true); return; }
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { rootMargin: "240px" } // start loading just before it scrolls into view
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [visible]);
  if (visible) return <>{children}</>;
  return (
    <div
      ref={ref}
      data-testid={`lazy-section-${name}`}
      className="rounded-2xl border border-neutral-900 bg-neutral-950/40 px-4 py-6 cursor-pointer hover:border-neutral-700"
      onClick={show}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); show(); } }}
    >
      <p className="label-eyebrow">{title}</p>
      <p className="text-xs text-neutral-600 mt-1">loads on scroll — click to load now</p>
    </div>
  );
}

// ============================================================================
// STAGE 2 founder view (2026-10-06) — four primary landing sections, in order:
// TODAY / PLAYER CHAIN / MONEY / ANOMALIES.
//
// Presentation-only: every number comes from the already-fetched
// mega-endpoint payload or the existing admin read endpoints
// (/admin/player-chain, /founder/acquisition-funnel). No metric definition
// is changed here; auth is untouched. The player-chain endpoint is built in
// parallel — SectionPlayerChain renders a clean loading/empty/error state
// when it is not available yet and never breaks the page.
// ============================================================================

// ---- Shared fetch: per-platform purchase funnels (7d) + android 24h -------
// Backed by the existing /founder/acquisition-funnel endpoint (Iter XX.198),
// which already splits by platform and already includes the `checkout_failed`
// step (P0 pre-sheet diagnostic data, incl. the error_message extra). Called
// once at the top level and shared by MONEY + ANOMALIES so the landing fires
// 4 small parallel GETs instead of 8.
export function useMoneyFunnels(refreshKey) {
  const [state, setState] = useState({
    loading: true, err: null, byPlatform: {}, android24h: null,
  });
  useEffect(() => {
    let alive = true;
    async function load() {
      try {
        const reqs = ["web", "android", "ios"].map((p) =>
          api.get("/founder/acquisition-funnel", { params: { days: 7, platform: p } })
        );
        // 24h android slice exists so the P0 pre-sheet row has a day number.
        reqs.push(
          api.get("/founder/acquisition-funnel", { params: { days: 1, platform: "android" } })
        );
        const [web, android, ios, android24] = await Promise.all(reqs);
        if (!alive) return;
        const byStep = (r) => {
          const m = {};
          ((r && r.data && r.data.steps) || []).forEach((s) => {
            m[s.step] = (typeof s.unique_users === "number") ? s.unique_users : 0;
          });
          return m;
        };
        setState({
          loading: false, err: null,
          byPlatform: { web: byStep(web), android: byStep(android), ios: byStep(ios) },
          android24h: byStep(android24),
        });
      } catch (e) {
        if (alive) setState({
          loading: false,
          err: (e && e.response && e.response.data && e.response.data.detail) || "Could not load funnel data.",
          byPlatform: {}, android24h: null,
        });
      }
    }
    load();
    return () => { alive = false; };
  }, [refreshKey]);
  return state;
}

// ---- Anomaly derivation (pure — easy to unit-test) -------------------------
// Priority order is founder-attention order, not log order. Everything is
// derived from the mega payload's existing audit_events queries plus the
// acquisition-funnel checkout_failed read — no new instrumentation.
export function computeAnomalies(data, funnels) {
  const items = [];
  const t = (data && data.today_24h) || {};
  const w = (data && data.last_7d) || {};
  const vh = (data && data.video_health) || {};
  const rev = (data && data.revenue) || {};
  const leak = (data && data.commercial_north_star && data.commercial_north_star.biggest_leak) || {};

  // 1. Android pre-sheet purchase failures — P0. Money on fire goes first.
  const androidFailed24h = funnels && funnels.android24h ? funnels.android24h.checkout_failed : null;
  if (androidFailed24h != null && androidFailed24h > 0) {
    items.push({
      id: "android-presheet", severity: "critical",
      title: `Android pre-sheet purchase failures: ${androidFailed24h} in 24h`,
      detail: "checkout_failed on Android before the Play sheet appeared. The P0 diagnostic (error_message) is live — classify via the P0 workflow, not this tile.",
      where: "MONEY → android pre-sheet row",
    });
  }

  // 2. Payment failures (Stripe / payment_failed / stripe_error audit events).
  const payFailed = num(rev.payments_failed_24h);
  if (payFailed > 0) {
    items.push({
      id: "payments-failed", severity: "high",
      title: `${payFailed} payment ${plural(payFailed, "failure", "failures")} in 24h`,
      detail: "payment_failed / stripe_error audit events in the last 24h. Check Stripe for decline reasons.",
      where: "MONEY → today",
    });
  }

  // 3. Video reliability — core product, fix before pushing traffic.
  const uploads = num(t.videos_uploaded);
  const todayRate = t.review_success_rate_pct;
  const windowRate = vh.success_rate_pct;
  if ((uploads >= 5 && typeof todayRate === "number" && todayRate < 75) ||
      (typeof windowRate === "number" && windowRate < 75 && num(vh.total_uploaded) >= 10)) {
    const rate = (uploads >= 5 && typeof todayRate === "number") ? todayRate : windowRate;
    items.push({
      id: "video-reliability", severity: "high",
      title: `Video completion below bar (${rate}%)`,
      detail: "Review success under 75%. Video review is core — investigate before pushing traffic.",
      where: "all dashboards → video",
    });
  }

  // 4. Biggest commercial leak (the founder signal the north-star already computes).
  const strength = leak.signal_strength;
  if ((strength === "high" || strength === "medium") && leak.stage_from) {
    items.push({
      id: "commercial-leak", severity: strength === "high" ? "high" : "medium",
      title: `Biggest commercial leak: ${leak.stage_from} → ${leak.stage_to}`,
      detail: leak.one_line || "",
      where: "more detail → commercial north star",
    });
  }

  // 5. Activation collapse.
  const sessions = num(t.web_sessions);
  const firstMsg = num(t.first_messages);
  const fmRate = sessions > 0 ? (firstMsg / sessions) * 100 : null;
  if (sessions >= 10 && fmRate != null && fmRate < 25) {
    items.push({
      id: "activation", severity: "medium",
      title: `Activation weak — ${fmRate.toFixed(1)}% of sessions sent a first message`,
      detail: "Fewer than 1 in 4 sessions start talking. Check the first-turn experience.",
      where: "TODAY",
    });
  }

  // 6. No returns — traffic without habit.
  if (sessions >= 10 && num(t.returning_users) === 0 && num(w.returning_users) === 0) {
    items.push({
      id: "no-returns", severity: "medium",
      title: "No returning users — today or the last 7 days",
      detail: "Traffic is arriving but nobody is coming back. Habit signal is flat.",
      where: "TODAY / PLAYER CHAIN",
    });
  }

  return items;
}

// ---- 1. TODAY ---------------------------------------------------------------
export function SectionToday({ data, anomalyCount }) {
  const t = (data && data.today_24h) || {};
  const w = (data && data.last_7d) || {};
  const rev = (data && data.revenue) || {};
  const plusChain = `${num(t.paywall_views)} → ${num(t.checkout_starts)} → ${num(t.payments_completed)}`;
  return (
    <SectionHeader title="today" subtitle="last 24 hours · the 10-second read">
      <div data-testid="founder-today">
        <Grid cols="6">
          <Tile k="players in" v={num(t.new_users)} id="today-players-in"
                sub={`${num(t.linked_users)} linked sessions`} />
          <Tile k="engaged · first msg" v={num(t.first_messages)} id="today-engaged"
                sub={`${num(t.user_messages_total)} user msgs`} />
          <Tile k="returned" v={num(t.returning_users)} id="today-returned"
                sub={`${num(w.returning_users)} in 7d`} />
          <Tile k="plus: views → starts → paid" v={plusChain} id="today-plus-chain"
                sub="exposure → attempt → completion" />
          <Tile k="payments failed" v={num(rev.payments_failed_24h)} id="today-pay-failed"
                sub="stripe / payment_failed" />
          <Tile k="anomalies firing" v={anomalyCount || 0} id="today-anomalies"
                sub="see anomalies below" />
        </Grid>
        <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-neutral-500" data-testid="today-caveats">
          <span>
            top sources:{" "}
            <span className="text-neutral-400">not instrumented in the landing payload</span>
            {" "}(attribution wiring is a known gap)
          </span>
          <a href="#player-chain"
             className="text-neutral-300 underline decoration-neutral-700 underline-offset-2 hover:text-neutral-100">
            prescriptions &amp; report-backs live per-player in the player chain ↓
          </a>
        </div>
        <div className="mt-6">
          <SectionFounderRead data={data} />
        </div>
      </div>
    </SectionHeader>
  );
}

// ---- 2. PLAYER CHAIN --------------------------------------------------------
// Designed against the parallel backend spec: GET /admin/player-chain
// (codebase path: /admin/player-chain) returns ~20 recent players; each row
// carries source / first_seen / problems / prescription / returned /
// report_back / debrief_outcome / plus_exposure / purchase_attempt / purchase
// / flags / identity_status / journey_url. The component is defensive about
// the wrapper shape and renders a clean loading/empty/error state when the
// endpoint is not available yet — the rest of the page is unaffected.
const PLAYER_CHAIN_LIMIT = 20;

function pcToList(v) {
  if (Array.isArray(v)) return v.filter((x) => x != null && x !== "").map(String);
  if (v == null || v === "") return [];
  return [String(v)];
}
function pcText(v) {
  if (v == null) return "";
  if (typeof v === "string") return v;
  if (typeof v.text === "string") return v.text;
  if (typeof v.summary === "string") return v.summary;
  try { return JSON.stringify(v); } catch { return ""; }
}
function pcTrunc(s, n) {
  const str = String(s || "");
  return str.length > n ? str.slice(0, n - 1) + "…" : str;
}
function pcShortId(id) {
  const s = String(id || "");
  return s.length > 10 ? s.slice(0, 8) + "…" : s;
}

function IdentityPill({ status }) {
  const s = String(status || "unknown").toLowerCase();
  // anonymous / unlinked get the loudest pill — the founder must never read
  // a row as a merged identity it isn't.
  const anon = s === "anonymous" || s === "unlinked" || s === "unknown";
  return (
    <span
      data-testid={`identity-${s}`}
      className={`ml-2 inline-block rounded-full px-2 py-0.5 text-[10px] font-medium border ${
        anon
          ? "bg-amber-950/50 text-amber-200/90 border-amber-900/60"
          : "bg-neutral-900 text-neutral-400 border-neutral-800"
      }`}
    >
      {s}
    </span>
  );
}

function PlayerChainRow({ row, index, expanded, onToggle }) {
  const rowId = row.actor_id || row.player_id || String(index);
  const label = row.player_label || row.label || (row.actor_id ? pcShortId(row.actor_id) : `player ${index + 1}`);
  const problems = pcToList(row.problems);
  const flags = pcToList(row.flags);
  const prescription = pcText(row.prescription);
  const purchaseStage = row.purchase
    ? `paid${row.purchase_attempt ? " (attempted)" : ""}`
    : (row.purchase_attempt ? "attempted" : "—");
  return (
    <React.Fragment>
      <tr
        className="border-b border-neutral-900/60 hover:bg-neutral-900/40 cursor-pointer"
        onClick={onToggle}
        data-testid={`player-chain-row-${index}`}
      >
        <td className="px-3 py-2.5 whitespace-nowrap">
          <span className="text-neutral-100 font-medium">{label}</span>
          <IdentityPill status={row.identity_status} />
        </td>
        <td className="px-3 py-2.5 text-neutral-400">{row.source || "—"}</td>
        <td className="px-3 py-2.5 text-neutral-400 whitespace-nowrap">{shortTime(row.first_seen) || "—"}</td>
        <td className="px-3 py-2.5 text-neutral-300 max-w-[220px]">
          {problems.length === 0 ? "—" : problems.slice(0, 3).map((p, i) => (
            <span key={i} className="inline-block mr-1 mb-1 rounded bg-neutral-900 border border-neutral-800 px-1.5 py-0.5 text-[10px]">
              {pcTrunc(p, 40)}
            </span>
          ))}
          {problems.length > 3 && <span className="text-neutral-500 text-[10px]">+{problems.length - 3}</span>}
        </td>
        <td className="px-3 py-2.5 text-neutral-300 max-w-[260px]">{prescription ? pcTrunc(prescription, 80) : "—"}</td>
        <td className="px-3 py-2.5">{row.returned ? <span className="text-emerald-300">yes</span> : <span className="text-neutral-600">—</span>}</td>
        <td className="px-3 py-2.5 text-neutral-300">{row.debrief_outcome || "—"}</td>
        <td className="px-3 py-2.5 text-neutral-300">{row.plus_exposure || "—"}</td>
        <td className="px-3 py-2.5 text-neutral-300 whitespace-nowrap">{purchaseStage}</td>
        <td className="px-3 py-2.5">
          {flags.length === 0 ? <span className="text-neutral-600">—</span> : flags.map((f, i) => (
            <span key={i} className="inline-block mr-1 mb-1 rounded bg-red-950/40 border border-red-900/50 text-red-200/90 px-1.5 py-0.5 text-[10px]">
              {f}
            </span>
          ))}
        </td>
      </tr>
      {expanded && (
        <tr data-testid={`player-chain-detail-${index}`}>
          <td colSpan={10} className="px-4 py-4 bg-neutral-950/70 border-b border-neutral-800">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
              <div>
                <p className="label-eyebrow mb-1">prescription</p>
                <p className="text-neutral-200 leading-relaxed whitespace-pre-wrap">{prescription || "—"}</p>
              </div>
              <div>
                <p className="label-eyebrow mb-1">report-back</p>
                <p className="text-neutral-200 leading-relaxed whitespace-pre-wrap">{pcText(row.report_back) || "—"}</p>
              </div>
              <div>
                <p className="label-eyebrow mb-1">problems</p>
                <p className="text-neutral-300">{problems.length ? problems.join(" · ") : "—"}</p>
              </div>
              <div>
                <p className="label-eyebrow mb-1">journey</p>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="text-neutral-500">actor <code>{pcShortId(row.actor_id || row.player_id)}</code></span>
                  {row.journey_url ? (
                    <a href={row.journey_url} target="_blank" rel="noreferrer"
                       className="text-emerald-300 underline decoration-emerald-900 underline-offset-2 hover:text-emerald-200"
                       data-testid={`player-chain-journey-${index}`}>
                      open journey ↗
                    </a>
                  ) : (
                    <span className="text-neutral-600">no journey link supplied by the endpoint</span>
                  )}
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </React.Fragment>
  );
}

export function SectionPlayerChain({ refreshKey }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    let alive = true;
    async function load() {
      setLoading(true);
      try {
        // Codebase path for GET /api/admin/player-chain (axios baseURL ends in /api).
        const { data } = await api.get("/admin/player-chain", {
          params: { limit: PLAYER_CHAIN_LIMIT },
          timeout: 30000,
        });
        const list = Array.isArray(data) ? data
          : Array.isArray(data && data.rows) ? data.rows
          : Array.isArray(data && data.players) ? data.players
          : null;
        if (!alive) return;
        if (list == null) throw new Error("unexpected player-chain response shape");
        setRows(list); setErr(null);
      } catch (e) {
        if (!alive) return;
        const status = e && e.response && e.response.status;
        setErr(status === 404
          ? "player-chain endpoint not available yet (backend lane in progress)"
          : ((e && e.response && e.response.data && e.response.data.detail) || (e && e.message) || "Could not load player chain."));
        setRows([]);
      } finally {
        if (alive) setLoading(false);
      }
    }
    load();
    return () => { alive = false; };
  }, [refreshKey]);

  const idOf = (row, i) => row.actor_id || row.player_id || String(i);

  return (
    <SectionHeader title="player chain" subtitle="one row per player · recent players first · the primary view">
      <div id="player-chain" data-testid="player-chain">
        {loading && (
          <div className="surface-1 p-6 text-sm text-neutral-500" data-testid="player-chain-loading">
            Loading player chain…
          </div>
        )}
        {!loading && err && (
          <div className="surface-1 p-6 text-sm" data-testid="player-chain-error">
            <p className="text-neutral-300">{err}</p>
            <p className="mt-2 text-xs text-neutral-500">
              The rest of the dashboard is unaffected. The parallel backend lane is building{" "}
              <code>GET /api/admin/player-chain</code>; this section will light up when it lands.
            </p>
          </div>
        )}
        {!loading && !err && rows && rows.length === 0 && (
          <div className="surface-1 p-6 text-sm text-neutral-500" data-testid="player-chain-empty">
            No players in the current window.
          </div>
        )}
        {!loading && !err && rows && rows.length > 0 && (
          <div className="surface-1 overflow-x-auto" data-testid="player-chain-table-wrap">
            <table className="w-full text-xs min-w-[1100px]" data-testid="player-chain-table">
              <thead>
                <tr className="text-left border-b border-neutral-900">
                  {["player", "source", "first seen", "problems", "prescription", "returned?", "debrief", "plus stage", "purchase", "flags"].map((h) => (
                    <th key={h} className="px-3 py-2 font-medium uppercase tracking-wider text-[10px] text-neutral-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => {
                  const rid = idOf(row, i);
                  return (
                    <PlayerChainRow
                      key={rid} row={row} index={i}
                      expanded={expandedId === rid}
                      onToggle={() => setExpandedId((prev) => (prev === rid ? null : rid))}
                    />
                  );
                })}
              </tbody>
            </table>
            <p className="px-4 py-2 text-[11px] text-neutral-600">
              click a row to inspect the journey behind the aggregate · identity shown exactly as the endpoint reports it — never merged
            </p>
          </div>
        )}
      </div>
    </SectionHeader>
  );
}

// ---- 3. MONEY ----------------------------------------------------------------
// Plus funnel day + 7d from existing data only: the mega payload's revenue
// section (24h) plus the existing /founder/acquisition-funnel read
// (per-platform, 7d). Android pre-sheet failures are a DISTINCT row sourced
// from the existing P0 checkout_failed data — never buried in generic
// payment failures.
const MONEY_CHAIN_STEPS = [
  "plus_paywall_viewed",
  "purchase_cta_tapped",
  "checkout_request_started",
  "checkout_session_created",
  "checkout_opened",
  "payment_completed",
];
const MONEY_FAIL_STEPS = ["checkout_failed", "checkout_cancelled"];

function PlatformFunnelCard({ platform, steps }) {
  const tone = platform === "android" ? "text-emerald-300" : "text-neutral-200";
  return (
    <div className="surface-1 p-4" data-testid={`money-platform-${platform}`}>
      <p className={`label-eyebrow mb-3 ${tone}`}>{platform} · 7d</p>
      <div className="space-y-1.5">
        {MONEY_CHAIN_STEPS.map((s) => (
          <div key={s} className="flex items-baseline justify-between gap-2 text-xs"
               data-testid={`money-${platform}-${s}`}>
            <span className="text-neutral-400">{s.replace(/_/g, " ")}</span>
            <span className="text-neutral-100 tabular-nums font-medium">{steps[s] ?? "—"}</span>
          </div>
        ))}
        <div className="pt-2 mt-2 border-t border-neutral-900 space-y-1.5">
          {MONEY_FAIL_STEPS.map((s) => (
            <div key={s} className="flex items-baseline justify-between gap-2 text-xs"
                 data-testid={`money-${platform}-fail-${s}`}>
              <span className="text-red-300/80">{s.replace(/_/g, " ")}</span>
              <span className="text-red-200 tabular-nums font-medium">{steps[s] ?? "—"}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function SectionMoney({ data, funnels }) {
  const rev = (data && data.revenue) || {};
  const w = (data && data.last_7d) || {};
  const f = funnels || {};
  const androidFailed24h = f.android24h ? f.android24h.checkout_failed : null;
  const androidFailed7d = f.byPlatform && f.byPlatform.android ? f.byPlatform.android.checkout_failed : null;

  return (
    <SectionHeader title="money" subtitle="plus funnel · day + 7d · per platform · pre-sheet failures kept distinct">
      <div data-testid="money">
        {/* 24h chain from the mega payload — no new queries */}
        <Grid cols="4">
          <Tile k="paywall shown 24h" v={num(rev.paywall_shown_24h)} id="money-shown-24h" />
          <Tile k="upgrade clicks 24h" v={num(rev.upgrade_clicks_24h)} id="money-clicks-24h" />
          <Tile k="checkout starts 24h" v={num(rev.checkout_starts_24h)} id="money-starts-24h" />
          <Tile k="payments completed 24h" v={num(rev.payments_completed_24h)} id="money-paid-24h" />
          <Tile k="payments failed 24h" v={num(rev.payments_failed_24h)} id="money-failed-24h" sub="stripe / payment_failed — generic failures" />
          <Tile k="active subscriptions" v={num(rev.active_subscriptions)} id="money-subs" />
          <Tile k="MRR" v={fmtMRR(rev.mrr_amount_minor, rev.mrr_currency)} id="money-mrr" />
          <Tile k="7d: views → starts → paid" v={`${num(w.paywall_views)} → ${num(w.checkout_starts)} → ${num(w.payments_completed)}`} id="money-7d-chain" />
        </Grid>

        {/* Android pre-sheet failures — DISTINCT row, never buried in generic failures */}
        <div
          className={`mt-4 rounded-xl border p-4 ${
            androidFailed24h > 0
              ? "border-red-900/70 bg-red-950/30"
              : "border-neutral-900 bg-neutral-950/40"
          }`}
          data-testid="money-android-presheet"
        >
          <div className="flex items-baseline justify-between gap-3 flex-wrap">
            <p className="label-eyebrow !text-red-200/90">android pre-sheet failures · P0 · distinct from generic payment failures</p>
            <p className="text-2xl font-light tabular-nums text-red-100" data-testid="money-android-presheet-count">
              {f.loading ? "…" : (androidFailed24h ?? "—")}
              <span className="text-xs text-neutral-400 ml-2">unique users · 24h</span>
            </p>
          </div>
          <p className="mt-2 text-xs text-neutral-400 leading-relaxed">
            Pre-sheet = <code>checkout_failed</code> on Android before the Play sheet ever appeared.
            7d android pre-sheet failures: <span className="text-neutral-200 tabular-nums">{f.loading ? "…" : (androidFailed7d ?? "—")}</span>.
            Diagnostic <code>error_message</code> lands on <code>checkout_failed</code> (deploy 786901d — backend half live; JS half needs the native build).
            Classify via the P0 workflow, not this tile.
          </p>
          {f.err && <p className="mt-2 text-xs text-amber-400/80">funnel read failed: {f.err}</p>}
        </div>

        {/* Per-platform 7d purchase funnels */}
        <p className="label-eyebrow mt-6 mb-3">purchase funnel by platform · 7d · unique users per step</p>
        {f.loading ? (
          <div className="surface-1 p-6 text-sm text-neutral-500" data-testid="money-funnels-loading">Loading platform funnels…</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="money-funnels">
            {["android", "ios", "web"].map((p) => (
              <PlatformFunnelCard key={p} platform={p} steps={(f.byPlatform && f.byPlatform[p]) || {}} />
            ))}
          </div>
        )}
        <p className="mt-3 text-[11px] text-neutral-600">
          Counts are unique users per step from the existing acquisition-funnel read (Iter XX.198).
          Purchase surface / source splits are not instrumented in that read.
        </p>
      </div>
    </SectionHeader>
  );
}

// ---- 4. ANOMALIES ------------------------------------------------------------
// Founder-attention prioritized — not a logging console. Short list, ordered
// by what needs the founder first. Debrief interruptions + detector/flow
// anomalies are covered by the revenue-pulse debrief watch (Stage 1) —
// there is no landing feed for them yet, so this section says so instead of
// faking one.
const ANOMALY_TONES = {
  critical: "border-red-900/70 bg-red-950/30",
  high:     "border-amber-900/60 bg-amber-950/25",
  medium:   "border-neutral-800 bg-neutral-900/50",
};
const ANOMALY_LABEL = { critical: "critical", high: "high", medium: "watch" };

export function SectionAnomalies({ items }) {
  const list = Array.isArray(items) ? items : [];
  return (
    <SectionHeader title="anomalies" subtitle="founder-attention prioritized · not a log console">
      <div data-testid="anomalies">
        {list.length === 0 ? (
          <div className="surface-1 p-6 text-sm text-neutral-500" data-testid="anomalies-empty">
            All quiet — no anomalies firing on current data.
          </div>
        ) : (
          <ul className="space-y-2" data-testid="anomalies-list">
            {list.map((a) => (
              <li
                key={a.id}
                className={`rounded-xl border p-4 ${ANOMALY_TONES[a.severity] || ANOMALY_TONES.medium}`}
                data-testid={`anomaly-${a.id}`}
                data-severity={a.severity}
              >
                <div className="flex items-baseline gap-3 flex-wrap">
                  <span className="text-[10px] uppercase tracking-widest text-neutral-400">
                    {ANOMALY_LABEL[a.severity] || a.severity}
                  </span>
                  <p className="text-sm font-medium text-neutral-100">{a.title}</p>
                </div>
                {a.detail && <p className="mt-1 text-xs text-neutral-400 leading-relaxed">{a.detail}</p>}
                {a.where && <p className="mt-1 text-[11px] text-neutral-600">where: {a.where}</p>}
              </li>
            ))}
          </ul>
        )}
        <p className="mt-3 text-[11px] leading-relaxed text-neutral-600" data-testid="anomalies-coverage">
          Debrief interruptions + detector/flow anomalies are covered by the revenue-pulse debrief watch (Stage 1) —
          no landing feed exists yet. Attribution breaks are not instrumented on the landing payload.
        </p>
      </div>
    </SectionHeader>
  );
}

// ============================================================================
// FOUNDER SIGNAL — Commercial North Star funnel (2026-02-26)
//
// Renders the cohort-matched commercial funnel produced by
// backend/commercial_signal.py. Sits at the very top of the dashboard.
// Default window 3d, optional 7d. No 30-day view here.
//
// Order (per founder brief):
//   1. FOUNDER SIGNAL — biggest commercial leak + one-line diagnosis
//   2. Cohort-matched commercial funnel
//   3. Acquisition context (session-level, kept separate)
//   4. 3 evidence conversations
//   5. WHAT SHOULD WE FIX NEXT? — exactly one recommendation
// ============================================================================
function SectionCommercialNorthStar({ nsf, currentWindow, onChangeWindow }) {
  if (!nsf) return null;
  const funnel = Array.isArray(nsf.funnel) ? nsf.funnel : [];
  const leak = nsf.biggest_leak || {};
  const evidence = nsf.evidence || {};
  const acq = nsf.acquisition_context || {};
  const strength = leak.signal_strength || "low";
  const strengthClasses = {
    high:                  "border-red-800/70 bg-red-950/40 text-red-100",
    medium:                "border-amber-800/70 bg-amber-950/40 text-amber-100",
    low:                   "border-neutral-700 bg-neutral-900/60 text-neutral-100",
    insufficient_sample:   "border-neutral-800 bg-neutral-950 text-neutral-400",
    error:                 "border-red-900/50 bg-red-950/30 text-red-300",
  }[strength] || "border-neutral-800 bg-neutral-900/60 text-neutral-100";

  return (
    <div className="space-y-4" data-testid="commercial-north-star">
      {/* ── FOUNDER SIGNAL header ── */}
      <div className={`rounded-lg border p-5 ${strengthClasses}`} data-testid="cns-founder-signal">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[11px] uppercase tracking-widest opacity-70">
              founder signal · {nsf.window_label || "last 3 days"}
            </p>
            <p className="mt-2 text-[15px] font-medium leading-snug">
              Biggest commercial leak:{" "}
              <span data-testid="cns-leak-stage" className="font-semibold">
                {leak.stage_from ? `${leak.stage_from} → ${leak.stage_to}` : "none identified"}
              </span>
            </p>
            <p
              className="mt-2 text-sm leading-relaxed opacity-90"
              data-testid="cns-leak-one-line"
            >
              {leak.one_line || "Not enough data yet."}
            </p>
          </div>
          <div className="flex flex-col gap-1 text-xs shrink-0">
            {["3d", "7d"].map((w) => (
              <button
                key={w}
                onClick={() => onChangeWindow(w)}
                className={`px-3 py-1.5 rounded border ${
                  w === currentWindow
                    ? "bg-neutral-800 border-neutral-700 text-neutral-100"
                    : "bg-neutral-950 border-neutral-900 text-neutral-500 hover:text-neutral-300"
                }`}
                data-testid={`cns-window-${w}`}
              >
                {w}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Cohort-matched commercial funnel ── */}
      <div className="surface-1 p-5 space-y-3" data-testid="cns-funnel">
        <div className="flex items-baseline justify-between">
          <p className="label-eyebrow">commercial funnel · cohort-matched · user-level</p>
          <p className="text-[11px] text-neutral-500">
            registered users in {nsf.window_label} = cohort denominator
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-6 gap-2" data-testid="cns-funnel-rows">
          {funnel.map((row) => (
            <div
              key={row.stage}
              className="rounded border border-neutral-800 bg-neutral-950/60 p-3"
              data-testid={`cns-stage-${row.stage}`}
            >
              <p className="text-[11px] uppercase tracking-wider text-neutral-500">
                {row.stage.replace(/_/g, " ")}
              </p>
              <p className="mt-1 text-2xl font-semibold text-neutral-100">
                {row.count}
              </p>
              <p className="text-[11px] text-neutral-500 mt-0.5">
                {row.pct_of_previous == null
                  ? <span>&nbsp;</span>
                  : `${Math.round(row.pct_of_previous * 100)}% of prev`}
              </p>
            </div>
          ))}
        </div>
        <details className="text-[11px] text-neutral-500">
          <summary className="cursor-pointer hover:text-neutral-300">
            cohort definitions (verbatim from backend)
          </summary>
          <ul className="mt-2 space-y-1 pl-4 list-disc" data-testid="cns-cohort-defs">
            {Object.entries(nsf.cohort_definitions || {}).map(([k, v]) => (
              <li key={k}><span className="text-neutral-400">{k}</span> — {v}</li>
            ))}
          </ul>
        </details>
      </div>

      {/* ── Acquisition context (session-level, kept SEPARATE) ── */}
      <div className="surface-1 p-5" data-testid="cns-acquisition">
        <p className="label-eyebrow">acquisition context · session-level · not joined to funnel</p>
        <div className="grid grid-cols-3 gap-4 mt-3">
          <div>
            <p className="text-[11px] uppercase tracking-wider text-neutral-500">landing sessions</p>
            <p className="text-2xl font-semibold text-neutral-100">{acq.landing_sessions ?? "—"}</p>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-wider text-neutral-500">session registrations</p>
            <p className="text-2xl font-semibold text-neutral-100">{acq.session_registrations ?? "—"}</p>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-wider text-neutral-500">registration rate</p>
            <p className="text-2xl font-semibold text-neutral-100">
              {acq.registration_rate_sessionlevel == null
                ? "—"
                : `${Math.round(acq.registration_rate_sessionlevel * 100)}%`}
            </p>
          </div>
        </div>
        <p className="text-[11px] text-neutral-500 mt-3">{acq.note}</p>
      </div>

      {/* ── Evidence — 3 real conversations ── */}
      <div className="surface-1 p-5 space-y-3" data-testid="cns-evidence">
        <p className="label-eyebrow">evidence · 3 conversations to inspect</p>
        <EvidenceCard title="Best value moment" ev={evidence.best_value_moment}
                       testId="cns-evidence-best-value" />
        <EvidenceCard title="Closest to subscribing" ev={evidence.closest_to_subscribing}
                       testId="cns-evidence-closest-subscribing" />
        <EvidenceCard title="Biggest drop-off" ev={evidence.biggest_drop_off}
                       testId="cns-evidence-drop-off" />
      </div>

      {/* ── What should we fix next? ── */}
      <div
        className="rounded-lg border border-emerald-900/60 bg-emerald-950/30 p-5"
        data-testid="cns-next-action"
      >
        <p className="text-[11px] uppercase tracking-widest text-emerald-300/80">
          what should we fix next?
        </p>
        <p className="mt-2 text-sm text-emerald-100 leading-relaxed">
          {nsf.next_action || "No recommendation."}
        </p>
      </div>
    </div>
  );
}

function EvidenceCard({ title, ev, testId }) {
  if (!ev) {
    return (
      <div className="rounded border border-neutral-800 bg-neutral-950/60 p-3" data-testid={testId}>
        <p className="text-[11px] uppercase tracking-wider text-neutral-500">{title}</p>
        <p className="text-sm text-neutral-500 mt-1">No candidate in this window.</p>
      </div>
    );
  }
  return (
    <div className="rounded border border-neutral-800 bg-neutral-950/60 p-3" data-testid={testId}>
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <p className="text-[11px] uppercase tracking-wider text-neutral-400">{title}</p>
        {ev.session_link ? (
          <a
            href={ev.session_link}
            className="text-[11px] text-emerald-300 hover:text-emerald-200 underline underline-offset-2"
            target="_blank" rel="noreferrer"
            data-testid={`${testId}-link`}
          >
            open session →
          </a>
        ) : null}
      </div>
      <div className="mt-1 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-neutral-300">
        {ev.user_id_hash && <div><span className="text-neutral-500">user</span> {ev.user_id_hash}</div>}
        {ev.session_id && <div><span className="text-neutral-500">session</span> {ev.session_id}</div>}
        {ev.outcome_reason && <div><span className="text-neutral-500">reason</span> {ev.outcome_reason}</div>}
        {ev.confidence_band && <div><span className="text-neutral-500">confidence</span> {ev.confidence_band}</div>}
        {ev.completed_at && <div><span className="text-neutral-500">completed at</span> {ev.completed_at}</div>}
        {ev.arc_lock_hits != null && <div><span className="text-neutral-500">arc-lock hits</span> {ev.arc_lock_hits}</div>}
        {ev.sub_kind && <div><span className="text-neutral-500">sub-kind</span> {ev.sub_kind}</div>}
        {ev.checkout_started_at && <div><span className="text-neutral-500">checkout at</span> {ev.checkout_started_at}</div>}
      </div>
      {ev.note && <p className="text-[11px] text-neutral-500 mt-2 leading-relaxed">{ev.note}</p>}
    </div>
  );
}


// ============================================================================
// Section 0 — Today's Founder Read (2026-06-26)
//
// Plain-English daily synthesis at the very top of the Web Control Room.
// Pure client-side derivation from `data.today_24h`, `data.last_7d`, and
// `data.video_health` — NO new backend, NO new tracking, NO new charts.
// Answers two questions in one glance:
//   1. What happened today?
//   2. What should I do next?
//
// Rules: one short paragraph or ≤5 bullets. One main issue, not ten.
// Always ends with one recommended founder action (build / observe /
// distribute / fix-first / hold-on-paid).
function SectionFounderRead({ data }) {
  const read = computeFounderRead(data);
  // Tone of the action chip — colour signals urgency, not severity.
  const toneClasses = {
    fix:        "border-red-900/60 bg-red-950/40 text-red-200",
    observe:    "border-amber-900/60 bg-amber-950/40 text-amber-200",
    distribute: "border-emerald-900/60 bg-emerald-950/40 text-emerald-200",
    hold:       "border-neutral-800 bg-neutral-900/60 text-neutral-200",
  }[read.action_tone] || "border-neutral-800 bg-neutral-900/60 text-neutral-200";

  return (
    <SectionHeader
      title="today's founder read"
      subtitle="plain-english · what happened · what to do next"
    >
      <div className="surface-1 p-5" data-testid="founder-read">
        <p
          className="text-[15px] leading-relaxed text-neutral-100"
          data-testid="founder-read-paragraph"
        >
          {read.paragraph}
        </p>
        <div
          className={`mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded border text-xs font-medium ${toneClasses}`}
          data-testid="founder-read-action"
        >
          <span className="label-eyebrow !text-current opacity-70">founder action</span>
          <span>{read.action}</span>
        </div>
      </div>
    </SectionHeader>
  );
}

// Pure function — easy to unit-test from JS / snapshot from a JSON fixture.
// All inputs sourced from existing founder-dashboard payload fields.
function computeFounderRead(data) {
  const t = data?.today_24h || {};
  const w = data?.last_7d || {};
  const vh = data?.video_health || {};

  const sessions = num(t.web_sessions);
  const firstMessages = num(t.first_messages);
  const returningUsers = num(t.returning_users);
  const videosUploaded = num(t.videos_uploaded);
  const reviewsCompleted = num(t.reviews_completed);
  const reviewsRejected = num(t.reviews_rejected);
  const paymentsCompleted = num(t.payments_completed);
  const paywallViews = num(t.paywall_views);
  const checkoutStarts = num(t.checkout_starts);

  // Video success: prefer today's rate, but it's volatile on low N — when
  // today has <5 uploads, fall back to the windowed video_health rate.
  const todayDenom = reviewsCompleted + reviewsRejected;
  const todaySuccessPct = todayDenom > 0
    ? Math.round((reviewsCompleted / todayDenom) * 1000) / 10
    : null;
  const windowSuccessPct = (typeof vh.success_rate_pct === "number")
    ? vh.success_rate_pct
    : null;
  const effectiveSuccessPct = (videosUploaded >= 5 && todaySuccessPct != null)
    ? todaySuccessPct
    : windowSuccessPct;
  const videoSampleEnough = (videosUploaded >= 5) || (num(vh.total_uploaded) >= 10);

  // Activation: a "first message" means a brand-new visitor actually
  // started talking. Anything ≥50% of sessions sending one is healthy
  // for a new product; <25% is a clear activation problem.
  const firstMsgRatePct = sessions > 0
    ? Math.round((firstMessages / sessions) * 1000) / 10
    : null;
  const activationWeak = (sessions >= 10) && (firstMsgRatePct != null) && (firstMsgRatePct < 25);

  // -- Compose the read --------------------------------------------------
  // Sentence 1: what happened today (always grounded in real numbers).
  const happened = sessions === 0
    ? "Quiet day so far — no web sessions yet."
    : `Today, ${sessions} ${plural(sessions, "session", "sessions")} came in and ${firstMessages} ${plural(firstMessages, "user", "users")} sent a first message.`;

  // Sentence 2: video activity (only if there were any uploads today, OR
  // the windowed rate is meaningfully bad — otherwise skip it).
  let videoLine = "";
  if (videosUploaded > 0) {
    const completed = reviewsCompleted;
    const failed = reviewsRejected;
    const ratePart = todaySuccessPct != null
      ? ` That's a ${todaySuccessPct}% success rate.`
      : "";
    videoLine = ` ${videosUploaded} ${plural(videosUploaded, "video was", "videos were")} uploaded, ${completed} completed, ${failed} failed.${ratePart}`;
  } else if (windowSuccessPct != null && windowSuccessPct < 75 && num(vh.total_uploaded) >= 5) {
    videoLine = ` No videos uploaded today, but the rolling video success rate is ${windowSuccessPct}% — below the 75% bar.`;
  }

  // Sentence 3: returning users / habit signal.
  let habitLine = "";
  if (returningUsers === 0 && sessions > 0) {
    habitLine = " Returning users are still 0 today, so Gully is getting trials but not yet showing habit.";
  } else if (returningUsers > 0) {
    habitLine = ` ${returningUsers} ${plural(returningUsers, "user", "users")} came back today.`;
  }

  // Decide the ONE main issue + action (priority order matters).
  // 1) Video reliability is core — fix first.
  // 2) Activation collapse beats everything else next.
  // 3) Zero returning users is a habit/retention flag (observe).
  // 4) Payments with weak reliability → hold paid.
  // 5) Everything healthy → distribute.
  let mainIssue, action, action_tone;
  if (videoSampleEnough && effectiveSuccessPct != null && effectiveSuccessPct < 75) {
    mainIssue = ` The biggest issue is video completion — ${effectiveSuccessPct}% success is too low for a product where video review is core.`;
    action = "Investigate video failures first — ship fixes before pushing content.";
    action_tone = "fix";
  } else if (activationWeak) {
    mainIssue = ` The biggest issue is activation — only ${firstMsgRatePct}% of sessions sent a first message.`;
    action = "Investigate the first-turn experience before pushing traffic.";
    action_tone = "fix";
  } else if (returningUsers === 0 && num(w.returning_users) === 0 && sessions >= 10) {
    mainIssue = " The risk is habit — neither today nor the last 7 days have produced returning users.";
    action = "Stop building and observe 24h — watch what's stopping people from coming back.";
    action_tone = "observe";
  } else if (paymentsCompleted === 0 && (paywallViews > 0 || checkoutStarts > 0) && effectiveSuccessPct != null && effectiveSuccessPct < 80) {
    mainIssue = " Paywall is firing but no payments are landing while product reliability is still weak.";
    action = "Do not push paid yet — close the reliability gap first.";
    action_tone = "hold";
  } else if (
    sessions >= 10
    && effectiveSuccessPct != null && effectiveSuccessPct >= 80
    && (firstMsgRatePct == null || firstMsgRatePct >= 25)
  ) {
    mainIssue = " No major reliability or activation issues showing.";
    action = "Push content today — product is stable enough.";
    action_tone = "distribute";
  } else if (sessions < 10) {
    mainIssue = " Sample is too small to call a main issue yet.";
    action = "Observe — wait for more traffic before deciding what to build.";
    action_tone = "observe";
  } else {
    mainIssue = " Nothing in the data is screaming for attention right now.";
    action = "Observe 24h — let signal build before the next move.";
    action_tone = "observe";
  }

  const paragraph = `${happened}${videoLine}${habitLine}${mainIssue}`;
  return { paragraph, action, action_tone };
}

function num(v) {
  return (typeof v === "number" && Number.isFinite(v)) ? v : 0;
}
function plural(n, singular, plural) {
  return n === 1 ? singular : plural;
}

// ============================================================================
// Section 1 — Overview
function Section1({ data }) {
  const t = data.today_24h, w = data.last_7d;
  return (
    <SectionHeader title="overview" subtitle="web product · current">
      <Grid cols="6">
        <Tile k="sessions today"        v={t.web_sessions}            id="s1-sess-24h" />
        <Tile k="linked users"          v={t.linked_users}            id="s1-linked-24h" />
        <Tile k="new users"             v={t.new_users}               id="s1-new-24h" />
        <Tile k="returning users"       v={t.returning_users}         id="s1-returning-24h" />
        <Tile k="first messages"        v={t.first_messages}          id="s1-firstmsg-24h" />
        <Tile k="user messages total"   v={t.user_messages_total}     id="s1-msgs-24h" />
        <Tile k="videos uploaded"       v={t.videos_uploaded}         id="s1-uploads-24h" />
        <Tile k="reviews completed"     v={t.reviews_completed}       id="s1-revcomp-24h" />
        <Tile k="reviews rejected"      v={t.reviews_rejected}        id="s1-revrej-24h" />
        <Tile k="success rate"          v={pct(t.review_success_rate_pct)} id="s1-succrate" />
        <Tile k="paywall views"         v={t.paywall_views}           id="s1-paywall-24h" />
        <Tile k="checkout starts"       v={t.checkout_starts}         id="s1-co-24h" />
        <Tile k="payments completed"    v={t.payments_completed}      id="s1-pay-24h" />
        <Tile k="active plus"           v={t.active_plus_users}       id="s1-plus-active" />
      </Grid>
      <p className="label-eyebrow mt-6 mb-2">last 7 days</p>
      <Grid cols="6">
        <Tile k="sessions 7d"           v={w.web_sessions}        id="s1-sess-7d" />
        <Tile k="first messages 7d"     v={w.first_messages}      id="s1-firstmsg-7d" />
        <Tile k="videos uploaded 7d"    v={w.videos_uploaded}     id="s1-uploads-7d" />
        <Tile k="successful reviews 7d" v={w.successful_reviews}  id="s1-success-7d" />
        <Tile k="repeat uploaders 7d"   v={w.repeat_uploaders}    id="s1-repeat-7d" />
        <Tile k="returning users 7d"    v={w.returning_users}     id="s1-return-7d" />
        <Tile k="paywall views 7d"      v={w.paywall_views}       id="s1-paywall-7d" />
        <Tile k="checkout starts 7d"    v={w.checkout_starts}     id="s1-co-7d" />
        <Tile k="payments 7d"           v={w.payments_completed}  id="s1-pay-7d" />
      </Grid>
    </SectionHeader>
  );
}

// ============================================================================
// Retention Investigation — read-only behavioural signals (no recommendations)
function SectionRetentionInvestigation() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [windowDays, setWindowDays] = useState(30);

  useEffect(() => {
    let alive = true;
    setData(null); setErr(null);
    api.get("/admin/web/retention-analysis", { params: { window_days: windowDays } })
      .then((r) => { if (alive) setData(r.data); })
      .catch((e) => { if (alive) setErr(e?.response?.data?.detail || "failed"); });
    return () => { alive = false; };
  }, [windowDays]);

  return (
    <SectionHeader
      title="retention investigation"
      subtitle="why are users not returning? · read-only behavioural signals"
    >
      <div className="flex items-center justify-between mb-px">
        <p className="text-xs text-neutral-500" data-testid="ri-subtitle">
          continuity is the cause · revenue is the outcome
        </p>
        <div className="flex gap-1 text-xs">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setWindowDays(d)}
              className={`px-2 py-1 rounded border ${
                d === windowDays
                  ? "bg-neutral-800 border-neutral-700 text-neutral-100"
                  : "bg-neutral-950 border-neutral-900 text-neutral-500 hover:text-neutral-300"
              }`}
              data-testid={`ri-window-${d}`}
            >{d}d</button>
          ))}
        </div>
      </div>
      {err && <div className="surface-1 p-4 text-red-400 text-sm" data-testid="ri-err">Error: {err}</div>}
      {!data && !err && <div className="surface-1 p-4 text-neutral-500 text-sm">Loading…</div>}
      {data && data.total_users_in_window === 0 && (
        <div className="surface-1 p-4 text-sm text-neutral-400" data-testid="ri-empty">
          <p className="mb-2">No linked users with messages in the last {data.window_days} days yet.</p>
          {(data.notes || []).map((n, i) => <p key={i} className="text-neutral-500 text-xs mt-1">{n}</p>)}
        </div>
      )}
      {data && data.total_users_in_window > 0 && (
        <>
          <Grid cols="4">
            <Tile k="linked users"          v={data.total_users_in_window}                       id="ri-total" />
            <Tile k="single-session users"  v={data.sessions.single_session_users}              id="ri-single" />
            <Tile k="multi-session users"   v={data.sessions.multi_session_users}               id="ri-multi" />
            <Tile k="return rate"           v={`${data.sessions.return_rate_pct}%`}             id="ri-return-rate" />
          </Grid>
          <Grid cols="4">
            <Tile k="returning · avg msgs"  v={data.returning.avg_messages}                     id="ri-avg-msgs" />
            <Tile k="returning · avg uploads" v={data.returning.avg_successful_uploads}         id="ri-avg-uploads" />
            <Tile k="avg continuity score"  v={data.returning.avg_continuity_score ?? "—"}      id="ri-avg-cont" />
            <Tile k="median hrs to return"  v={data.time_to_return_hours.median ?? "—"}         id="ri-ttr" />
          </Grid>

          <div className="grid md:grid-cols-2 gap-px mt-px"
               style={{ background: "var(--border-default)" }}>
            <div className="surface-1 p-4" data-testid="ri-return-predictors">
              <p className="label-eyebrow mb-3">behaviors predicting RETURN</p>
              {(data.return_predictors || []).length === 0 ? (
                <p className="text-sm text-neutral-500">Not enough variance yet.</p>
              ) : (
                <ul className="space-y-2 text-sm">
                  {data.return_predictors.map((p) => (
                    <li key={p.behavior} className="flex justify-between gap-3">
                      <span className="text-neutral-300">{p.behavior}</span>
                      <span className="font-mono text-emerald-300 whitespace-nowrap">
                        +{p.lift_vs_baseline_pp}pp · {p.return_rate_pct}% (n={p.n_users})
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="surface-1 p-4" data-testid="ri-churn-predictors">
              <p className="label-eyebrow mb-3">behaviors associated with CHURN</p>
              {(data.churn_predictors || []).length === 0 ? (
                <p className="text-sm text-neutral-500">Not enough variance yet.</p>
              ) : (
                <ul className="space-y-2 text-sm">
                  {data.churn_predictors.map((p) => (
                    <li key={p.behavior} className="flex justify-between gap-3">
                      <span className="text-neutral-300">{p.behavior}</span>
                      <span className="font-mono text-amber-300 whitespace-nowrap">
                        -{p.lift_vs_baseline_pp}pp · {p.churn_rate_pct}% churn (n={p.n_users})
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="surface-1 p-4 mt-px" data-testid="ri-earliest-signal">
            <p className="label-eyebrow mb-3">earliest return signal · return rate by 1st-session messages</p>
            <table className="w-full text-xs">
              <thead><tr className="text-neutral-500">
                <th className="text-left font-normal pb-1.5">1st session msgs</th>
                <th className="text-left font-normal pb-1.5">users</th>
                <th className="text-left font-normal pb-1.5">return rate</th>
              </tr></thead>
              <tbody>
                {(data.earliest_return_signal?.by_first_session_msgs || []).map((b) => (
                  <tr key={b.first_session_msgs} className="border-t border-neutral-900">
                    <td className="py-1.5 text-neutral-300 tabular-nums">{b.first_session_msgs}</td>
                    <td className="py-1.5 text-neutral-400 tabular-nums">{b.n}</td>
                    <td className="py-1.5 text-neutral-100 tabular-nums">{b.return_rate_pct}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="mt-3 text-xs text-neutral-400 italic">
              {data.earliest_return_signal?.hypothesis}
            </p>
          </div>

          <div className="surface-1 p-4 mt-px" data-testid="ri-last-action">
            <p className="label-eyebrow mb-3">most common last action before silence ≥40 days</p>
            {(data.last_action_before_churn || []).length === 0 ? (
              <p className="text-sm text-neutral-500">No churned users in this window.</p>
            ) : (
              <ul className="space-y-1.5 text-sm">
                {data.last_action_before_churn.map((r) => (
                  <li key={r.action} className="flex justify-between">
                    <span className="text-neutral-300">{(r.action || "").replace(/_/g, " ")}</span>
                    <span className="font-mono text-neutral-100">{r.count}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {(data.notes || []).length > 0 && (
            <div className="text-xs text-neutral-500 mt-px p-3 surface-1" data-testid="ri-notes">
              {data.notes.map((n, i) => <p key={i} className="leading-relaxed">{n}</p>)}
            </div>
          )}
        </>
      )}
    </SectionHeader>
  );
}


// Section 2 — Activation Funnel
function Section2Funnel({ funnel, windowLabel }) {
  return (
    <SectionHeader title="activation funnel" subtitle={`from landing to payment · ${windowLabel || "last 90 days"}`}>
      <div className="surface-1 divide-y divide-neutral-900" data-testid="s2-funnel">
        {funnel.map((row, i) => (
          <div key={row.step} className="px-4 py-3 flex items-center gap-4">
            <div className="flex-none text-xs text-neutral-600 w-5 tabular-nums">{i + 1}</div>
            <div className="flex-1 min-w-0">
              <div className="flex justify-between text-sm">
                <span className="text-neutral-200">{row.step.replace(/_/g, " ")}</span>
                <span className="text-neutral-400 text-xs tabular-nums">
                  {row.conv_pct == null ? "—" : `${row.conv_pct}% from prev`}
                </span>
              </div>
              <div className="mt-2 h-1.5 rounded-full bg-neutral-900 overflow-hidden">
                <div className="h-full bg-emerald-500/80" style={{ width: `${i === 0 ? 100 : Math.max(4, row.conv_pct ?? 4)}%` }} />
              </div>
            </div>
            <div className="flex-none font-mono text-base tabular-nums w-12 text-right">{row.count}</div>
          </div>
        ))}
      </div>
    </SectionHeader>
  );
}

// ============================================================================
// Iter XX.198 — Acquisition Funnel Tile
// Reads GET /api/founder/acquisition-funnel (admin-gated, backed by
// `acquisition_events`). Shows the 8 canonical steps + step-to-prev
// conversion %, filterable by platform (all / web / android / ios) and
// window (7 / 30 / 90 days).
//
// Data is INTERNAL source of truth. Meta ad-attribution is NOT wired
// in this build — see /app/memory/ATTRIBUTION_REALITY_XX198.md.
function SectionAcquisitionFunnel() {
  const [days, setDays] = useState(30);
  const [platform, setPlatform] = useState("all");
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    async function load() {
      setLoading(true);
      try {
        const params = { days };
        if (platform !== "all") params.platform = platform;
        const { data } = await api.get("/founder/acquisition-funnel", { params });
        if (alive) { setData(data); setErr(null); }
      } catch (e) {
        if (alive) setErr(e?.response?.data?.detail || "Could not load acquisition funnel.");
      } finally { if (alive) setLoading(false); }
    }
    load();
    return () => { alive = false; };
  }, [days, platform]);

  const steps = data?.steps || [];
  const first = steps[0]?.unique_users || 0;

  return (
    <SectionHeader
      title="acquisition funnel (Iter XX.198)"
      subtitle="canonical first_open → purchase · internal source of truth"
    >
      <div className="flex items-center gap-3 mb-3 flex-wrap" data-testid="acq-filters">
        <div className="flex gap-1" data-testid="acq-platform-toggle">
          {["all", "web", "android", "ios"].map((p) => (
            <button
              key={p}
              onClick={() => setPlatform(p)}
              data-testid={`acq-platform-${p}`}
              className={`px-2 py-1 text-xs rounded ${
                platform === p
                  ? "bg-emerald-500/20 text-emerald-300"
                  : "bg-neutral-900 text-neutral-400 hover:text-neutral-200"
              }`}
            >
              {p}
            </button>
          ))}
        </div>
        <div className="flex gap-1" data-testid="acq-days-toggle">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              data-testid={`acq-days-${d}`}
              className={`px-2 py-1 text-xs rounded ${
                days === d
                  ? "bg-emerald-500/20 text-emerald-300"
                  : "bg-neutral-900 text-neutral-400 hover:text-neutral-200"
              }`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {loading && !data && (
        <div className="surface-1 p-4 text-sm text-neutral-500" data-testid="acq-loading">
          Loading acquisition funnel…
        </div>
      )}
      {err && (
        <div className="surface-1 p-4 text-sm text-red-400" data-testid="acq-error">
          {err}
        </div>
      )}
      {!loading && !err && (
        <div className="surface-1 divide-y divide-neutral-900" data-testid="acq-funnel">
          {steps.map((row, i) => {
            const pctFromFirst = first > 0
              ? Math.max(4, Math.round((row.unique_users / first) * 100))
              : 4;
            return (
              <div key={row.step} className="px-4 py-3 flex items-center gap-4"
                   data-testid={`acq-row-${row.step}`}>
                <div className="flex-none text-xs text-neutral-600 w-5 tabular-nums">
                  {i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between text-sm">
                    <span className="text-neutral-200">
                      {row.step.replace(/_/g, " ")}
                    </span>
                    <span className="text-neutral-400 text-xs tabular-nums"
                          data-testid={`acq-conv-${row.step}`}>
                      {row.conversion_from_prev_pct == null
                        ? "—"
                        : `${row.conversion_from_prev_pct}% from prev`}
                    </span>
                  </div>
                  <div className="mt-2 h-1.5 rounded-full bg-neutral-900 overflow-hidden">
                    <div className="h-full bg-emerald-500/80"
                         style={{ width: `${pctFromFirst}%` }} />
                  </div>
                </div>
                <div className="flex-none font-mono text-base tabular-nums w-14 text-right"
                     data-testid={`acq-count-${row.step}`}>
                  {row.unique_users}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {data?.note && (
        <p className="mt-3 text-xs text-amber-400/80" data-testid="acq-note">
          {data.note}
        </p>
      )}
    </SectionHeader>
  );
}


// Section 3 — Video Review Health
function Section3Video({ health, windowLabel }) {
  return (
    <SectionHeader title="video review health" subtitle={`upload pipeline · gemini · ${windowLabel || "last 90 days"}`}>
      <Grid cols="4">
        <Tile k="uploaded"        v={health.total_uploaded}            id="s3-uploaded" />
        <Tile k="successful"      v={health.total_successful}          id="s3-success" />
        <Tile k="rejected"        v={health.total_rejected}            id="s3-rejected" />
        <Tile k="success rate"    v={pct(health.success_rate_pct)}     id="s3-rate" />
        <Tile k="median review"   v={fmtMs(health.median_review_ms)}   id="s3-median" />
        <Tile k="p95 review"      v={fmtMs(health.p95_review_ms)}      id="s3-p95" />
        <Tile k="pending"         v={health.pending_reviews}           id="s3-pending" />
        <Tile k="clarifications"  v={health.clarification_open}        id="s3-clar" />
      </Grid>
      <div className="surface-1 mt-px p-4" data-testid="s3-reasons">
        <p className="label-eyebrow mb-3">top rejection reasons</p>
        {!health.rejection_reasons_available ? (
          <p className="text-sm text-amber-300/90">
            Rejection reasons are not yet instrumented on <code>video_analyses</code>. Currently
            all failures fall back to <em>unspecified</em>.
          </p>
        ) : (
          <ul className="space-y-1.5">
            {health.rejection_reasons.map((r) => (
              <li key={r.reason} className="flex justify-between text-sm">
                <span className="text-neutral-300">{r.reason.replace(/_/g, " ")}</span>
                <span className="font-mono text-neutral-100">{r.count}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      {!health.review_time_instrumented && (
        <p className="mt-3 text-xs text-amber-400/80">
          Review-time timestamps not yet recorded — median / p95 fall back to payload.review_ms when present.
        </p>
      )}
    </SectionHeader>
  );
}

// ============================================================================
// Iter 59 / 59.1 — Stale Frontend Banner + Build Info.
// Founder choice 2d: log version mismatch everywhere (via the
// X-Frontend-Build-Id header on every request), but show a visible
// warning only inside /admin. We never block users — this is
// informational for the founder.
//
// Two display modes:
//   · "dev-unknown" frontend build → amber warning ("build id unknown")
//   · Both IDs present → compact info strip showing both build IDs +
//     deployed_at (no warning colour)
function StaleFrontendBanner() {
  const [info, setInfo] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get("/version").then(({ data }) => {
      if (alive) setInfo(data || null);
    }).catch(() => { /* silent — health endpoint must never break admin */ });
    return () => { alive = false; };
  }, []);
  if (!info) return null;

  const frontendBuild = FRONTEND_BUILD_ID;
  const isUnknown = frontendBuild === "dev-unknown";
  const backendBuild = info.backend_build_id || "unknown";
  const deployedAt = String(info.backend_deployed_at || "").slice(0, 19);

  if (isUnknown) {
    // Warning state — frontend was built without the build-id script.
    return (
      <div
        data-testid="stale-frontend-banner"
        className="rounded-2xl border border-amber-900/50 bg-amber-950/30 px-4 py-3 text-xs text-amber-200 flex items-start gap-3"
      >
        <div className="font-semibold text-amber-100">Frontend build id is unknown.</div>
        <div className="text-amber-200/80 flex-1">
          The `scripts/generate-build-id.js` prebuild script did not run, or `REACT_APP_BUILD_ID` was not set in the deploy pipeline.
          Set one of them so stale cached bundles can be detected in user error reports.
          <span className="ml-2 text-amber-300/70">
            backend_build_id: <code>{backendBuild}</code>
            {deployedAt && <> · deployed_at: <code>{deployedAt}</code></>}
          </span>
        </div>
      </div>
    );
  }

  // Healthy state — both IDs present, render a compact info strip so
  // the founder can confirm at a glance which build pair is live.
  return (
    <div
      data-testid="build-info-strip"
      className="rounded-2xl border border-neutral-900 bg-neutral-950/40 px-4 py-2 text-[11px] text-neutral-400 font-mono flex flex-wrap items-center gap-x-4 gap-y-1"
    >
      <span><span className="text-neutral-600">frontend_build_id:</span> <span className="text-emerald-300">{frontendBuild}</span></span>
      <span><span className="text-neutral-600">backend_build_id:</span> <span className="text-emerald-300">{backendBuild}</span></span>
      {deployedAt && (
        <span><span className="text-neutral-600">deployed_at:</span> <span className="text-neutral-300">{deployedAt}</span></span>
      )}
    </div>
  );
}


// ============================================================================
// Iter 59 — Client Ref Search.
// Founder workflow: a user pastes a `cref_...` from a screenshot; we
// look it up in the admin debug rows. This is just a thin search input
// on top of /api/admin/web/video-upload-debug?client_ref=<ref>.
function SectionClientRefSearch() {
  const [cref, setCref] = useState("");
  const [row, setRow] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);
  async function lookup(e) {
    if (e) e.preventDefault();
    const v = (cref || "").trim();
    if (!v) return;
    setLoading(true); setErr(null); setRow(null);
    try {
      const { data } = await api.get("/admin/web/video-upload-debug", {
        params: { client_ref: v, limit: 5 },
      });
      const rows = (data && data.rows) || [];
      if (!rows.length) { setErr("No matching row found."); }
      else { setRow(rows[0]); }
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Lookup failed.");
    } finally { setLoading(false); }
  }
  return (
    <SectionHeader
      title="trace by client_ref"
      subtitle="paste a cref_… from a user screenshot · returns the exact diagnostic row"
    >
      <form onSubmit={lookup} className="flex flex-wrap items-center gap-2">
        <input
          type="text"
          value={cref}
          onChange={(e) => setCref(e.target.value)}
          placeholder="cref_…"
          className="flex-1 min-w-[260px] px-3 py-2 rounded-lg border border-neutral-800 bg-neutral-950 text-neutral-100 placeholder-neutral-600 focus:outline-none focus:border-neutral-600 font-mono text-xs"
          data-testid="cref-search-input"
        />
        <button
          type="submit"
          disabled={loading || !cref.trim()}
          className="px-3 py-2 rounded-lg border border-emerald-900/60 bg-emerald-950/40 text-emerald-200 hover:bg-emerald-900/60 disabled:opacity-40 disabled:cursor-not-allowed text-xs"
          data-testid="cref-search-submit"
        >
          {loading ? "looking up…" : "look up"}
        </button>
      </form>
      {err && (
        <div className="mt-3 text-xs text-red-400" data-testid="cref-search-error">
          {err}
        </div>
      )}
      {row && (
        <div
          className="mt-3 rounded-2xl border border-neutral-900 bg-neutral-950/60 p-3 text-xs font-mono text-neutral-200 max-h-[420px] overflow-auto"
          data-testid="cref-search-result"
        >
          <pre className="whitespace-pre-wrap break-words">
            {JSON.stringify(row, null, 2)}
          </pre>
        </div>
      )}
    </SectionHeader>
  );
}


// ============================================================================
// Iter 59 — Video Health Scoreboard helpers (hoisted to module scope per
// react/no-unstable-nested-components).
function _VHRateColor(rate) {
  if (rate == null) return "text-neutral-500";
  if (rate >= 85) return "text-emerald-300";
  if (rate >= 65) return "text-amber-300";
  return "text-red-400";
}
function _VHStat({ label, value, testId, tone }) {
  return (
    <div className="rounded-xl border border-neutral-900 bg-neutral-950/50 p-3" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-neutral-500">{label}</div>
      <div className={`text-xl font-light mt-1 ${tone || "text-neutral-100"}`}>{value}</div>
    </div>
  );
}
function _VHTopList({ items, testId }) {
  if (!items || !items.length) {
    return <div className="text-xs text-neutral-600 italic">none</div>;
  }
  return (
    <ul className="space-y-1 text-xs font-mono" data-testid={testId}>
      {items.map((it, i) => (
        <li key={i} className="flex justify-between gap-3">
          <span className="text-neutral-300 truncate">{it.key}</span>
          <span className="text-neutral-500">{it.count}</span>
        </li>
      ))}
    </ul>
  );
}
function _VHWindow({ title, w, testId }) {
  if (!w) return null;
  return (
    <div className="space-y-3" data-testid={testId}>
      <div className="text-xs uppercase tracking-wider text-neutral-500">{title}</div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <_VHStat label="attempts" value={w.upload_attempts} testId={`${testId}-attempts`} />
        <_VHStat label="successes" value={w.successes} testId={`${testId}-successes`} tone="text-emerald-300" />
        <_VHStat label="failures" value={w.failures} testId={`${testId}-failures`} tone={w.failures > 0 ? "text-red-400" : "text-neutral-100"} />
        <_VHStat
          label="success rate"
          value={w.success_rate_pct == null ? "—" : `${w.success_rate_pct}%`}
          tone={_VHRateColor(w.success_rate_pct)}
          testId={`${testId}-rate`}
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="rounded-xl border border-neutral-900 bg-neutral-950/40 p-3">
          <div className="text-[10px] uppercase tracking-wider text-neutral-500 mb-2">top rejection reasons</div>
          <_VHTopList items={w.top_rejection_reasons} testId={`${testId}-top-reasons`} />
        </div>
        <div className="rounded-xl border border-neutral-900 bg-neutral-950/40 p-3">
          <div className="text-[10px] uppercase tracking-wider text-neutral-500 mb-2">top failure stages</div>
          <_VHTopList items={w.top_failure_stages} testId={`${testId}-top-stages`} />
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
        <_VHStat
          label="android (✓ / ✗)"
          value={`${w.os_split?.android?.success || 0} / ${w.os_split?.android?.failure || 0}`}
          testId={`${testId}-android`}
        />
        <_VHStat
          label="ios (✓ / ✗)"
          value={`${w.os_split?.ios?.success || 0} / ${w.os_split?.ios?.failure || 0}`}
          testId={`${testId}-ios`}
        />
        <_VHStat label="in-app browser fails" value={w.in_app_browser_failures || 0} testId={`${testId}-inapp`} />
        <_VHStat label="50-100MB fails" value={w.large_50_100mb_failures || 0} testId={`${testId}-large`} />
        <_VHStat label="retry clicks" value={w.retry_button_clicks || 0} testId={`${testId}-retries`} />
        <_VHStat label="repeat-failure sessions" value={w.repeat_failure_sessions || 0} testId={`${testId}-repeat`} />
      </div>
    </div>
  );
}


// ============================================================================
// Iter 59 — Video Health Scoreboard.
// Founder mandate: "I should not need users to DM me before knowing
// videos are unhealthy." Compact 24h + 7d snapshot. Refreshes on mount
// + every 60s. Pure read-only.
function SectionVideoHealthScoreboard() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let alive = true;
    async function load() {
      try {
        const { data } = await api.get("/admin/web/video-health");
        if (alive) { setData(data); setErr(null); }
      } catch (e) {
        if (alive) setErr(e?.response?.data?.detail || e.message || "failed");
      }
    }
    load();
    const t = setInterval(load, 60000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  return (
    <SectionHeader
      title="video health scoreboard"
      subtitle="24h + 7d snapshot · refreshes every 60s · web-only · smoke-filtered"
    >
      {err && <div className="text-xs text-red-400" data-testid="video-health-error">{err}</div>}
      {!data && !err && <div className="text-xs text-neutral-500">loading…</div>}
      {data && (
        <div className="space-y-6">
          <_VHWindow title="last 24 hours" w={data.last_24h} testId="vh-24h" />
          <_VHWindow title="last 7 days" w={data.last_7d} testId="vh-7d" />
          {data.last_10_failed_client_refs && data.last_10_failed_client_refs.length > 0 && (
            <div data-testid="vh-recent">
              <div className="text-xs uppercase tracking-wider text-neutral-500 mb-2">
                last 10 failed client_refs (48h)
              </div>
              <div className="rounded-xl border border-neutral-900 bg-neutral-950/40 overflow-x-auto">
                <table className="text-xs w-full">
                  <thead>
                    <tr className="text-neutral-500 border-b border-neutral-900">
                      <th className="text-left p-2 font-normal">timestamp</th>
                      <th className="text-left p-2 font-normal">client_ref</th>
                      <th className="text-left p-2 font-normal">rejection</th>
                      <th className="text-left p-2 font-normal">stage</th>
                      <th className="text-left p-2 font-normal">os</th>
                    </tr>
                  </thead>
                  <tbody className="font-mono">
                    {data.last_10_failed_client_refs.map((r, i) => (
                      <tr key={i} className="border-b border-neutral-900/50 last:border-b-0">
                        <td className="p-2 text-neutral-500">{String(r.timestamp || "").slice(0, 19)}</td>
                        <td className="p-2 text-neutral-300 break-all">{r.client_ref || "—"}</td>
                        <td className="p-2 text-amber-300">{r.rejection_reason || "—"}</td>
                        <td className="p-2 text-neutral-400">{r.failure_stage || "—"}</td>
                        <td className="p-2 text-neutral-400">{r.os || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <div className="text-[10px] text-neutral-600 font-mono">
            backend_build_id: {data.backend_build_id} · deployed_at: {String(data.backend_deployed_at || "").slice(0, 19)}
          </div>
        </div>
      )}
    </SectionHeader>
  );
}


// ============================================================================
// Iter XX.32 — Video review failure taxonomy (4 categories) + both
// raw-attempt and unique-upload failure rates. The unique-upload rate is
// the user-impact metric — it dedups retries so a single bad clip that
// eventually succeeded does not show up as a failure.
function SectionVideoFourCategory({ sinceDays = 30 }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let alive = true;
    async function load() {
      try {
        const { data } = await api.get(
          `/admin/video-dashboard/four-category?since_days=${sinceDays}`,
        );
        if (alive) { setData(data); setErr(null); }
      } catch (e) {
        if (alive) setErr(e?.response?.data?.detail || e.message || "failed");
      }
    }
    load();
    const t = setInterval(load, 60000);
    return () => { alive = false; clearInterval(t); };
  }, [sinceDays]);

  return (
    <SectionHeader
      title="video failure taxonomy (4-category)"
      subtitle={`since_days=${sinceDays} · raw vs unique-upload failure rate · Iter XX.32`}
    >
      {err && (
        <div className="text-xs text-red-400" data-testid="video-4cat-error">
          {err}
        </div>
      )}
      {!data && !err && (
        <div className="text-xs text-neutral-500">loading…</div>
      )}
      {data && (
        <div className="space-y-4" data-testid="video-4cat">
          <Grid cols="4">
            <Tile
              k="raw-attempt total"
              v={data.raw_attempt?.total ?? 0}
              id="v4c-raw-total"
            />
            <Tile
              k="raw-attempt failed"
              v={data.raw_attempt?.failed ?? 0}
              id="v4c-raw-failed"
            />
            <Tile
              k="raw-attempt failure rate"
              v={`${(data.raw_attempt?.failure_rate_pct ?? 0).toFixed(1)}%`}
              id="v4c-raw-rate"
            />
            <Tile
              k="recovered (retry OK)"
              v={data.unique_upload?.recovered ?? 0}
              id="v4c-recovered"
            />
            <Tile
              k="unique uploads"
              v={data.unique_upload?.total ?? 0}
              id="v4c-uniq-total"
            />
            <Tile
              k="unique-upload failed"
              v={data.unique_upload?.failed ?? 0}
              id="v4c-uniq-failed"
            />
            <Tile
              k="unique-upload failure rate ★"
              v={`${(data.unique_upload?.failure_rate_pct ?? 0).toFixed(1)}%`}
              id="v4c-uniq-rate"
            />
            <Tile
              k="unique-upload success"
              v={data.unique_upload?.success ?? 0}
              id="v4c-uniq-success"
            />
          </Grid>
          <_FourCategoryBreakdown
            title="raw-attempt failure breakdown"
            rows={data.raw_attempt?.per_category || []}
            testId="v4c-raw-breakdown"
          />
          <_FourCategoryBreakdown
            title="unique-upload failure breakdown"
            rows={data.unique_upload?.per_category || []}
            testId="v4c-uniq-breakdown"
          />
          <div className="text-[10px] text-neutral-600 font-mono">
            window_start: {String(data.window_start || "").slice(0, 19)}
          </div>
        </div>
      )}
    </SectionHeader>
  );
}

function _FourCategoryBreakdown({ title, rows, testId }) {
  const colours = {
    upload_validation:     "text-amber-300",
    model_failure:         "text-red-300",
    identity_continuation: "text-sky-300",
    recovered:             "text-emerald-300",
  };
  return (
    <div data-testid={testId}>
      <div className="text-xs uppercase tracking-wider text-neutral-500 mb-2">
        {title}
      </div>
      <div className="rounded-xl border border-neutral-900 bg-neutral-950/40 overflow-x-auto">
        <table className="text-xs w-full">
          <thead>
            <tr className="text-neutral-500 border-b border-neutral-900">
              <th className="text-left p-2 font-normal">bucket</th>
              <th className="text-right p-2 font-normal">count</th>
              <th className="text-right p-2 font-normal">pct</th>
            </tr>
          </thead>
          <tbody className="font-mono">
            {rows.map((r, i) => (
              <tr
                key={i}
                className="border-b border-neutral-900/50 last:border-b-0"
                data-testid={`${testId}-row-${r.bucket}`}
              >
                <td className={`p-2 ${colours[r.bucket] || "text-neutral-300"}`}>
                  {(r.bucket || "").replace(/_/g, " ")}
                </td>
                <td className="p-2 text-right text-neutral-300">{r.count}</td>
                <td className="p-2 text-right text-neutral-500">
                  {(r.pct ?? 0).toFixed(1)}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ============================================================================
// Video Upload Failures — reliability panel (P0 product reliability sprint)
function SectionUploadFailures() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [hours, setHours] = useState(168);
  // Filters
  const [fBrowser, setFBrowser] = useState("");
  const [fOs, setFOs] = useState("");
  const [fMime, setFMime] = useState("");
  const [fCodec, setFCodec] = useState("");

  const params = { limit: 50, hours };
  if (fBrowser) params.browser = fBrowser;
  if (fOs) params.os_name = fOs;
  if (fMime) params.mime_type = fMime;
  if (fCodec) params.codec = fCodec;

  useEffect(() => {
    let alive = true;
    setData(null); setErr(null);
    api.get("/admin/web/upload-failures", { params })
      .then(r => { if (alive) setData(r.data); })
      .catch((e) => { if (alive) setErr(e?.response?.data?.detail || "failed"); });
    return () => { alive = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hours, fBrowser, fOs, fMime, fCodec]);

  function exportCsv() {
    const q = new URLSearchParams({ ...params, format: "csv", limit: "500" });
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/admin/web/upload-failures?${q}`, "_blank");
  }

  /**
   * Iter 53.1 — row-level Failed-Video Debug export.
   * Hits `/api/admin/web/video-upload-debug` with `failed_only=true`
   * and the LAST 48 HOURS so the founder can RCA a fresh incident
   * without copy-pasting JWTs. Uses the authed `api` client so the
   * Bearer header rides the request — `window.open` can't carry it.
   */
  async function downloadDebug(fmt) {
    try {
      const resp = await api.get("/admin/web/video-upload-debug", {
        params: { failed_only: true, hours: 48, limit: 200, ...(fmt === "csv" ? { format: "csv" } : {}) },
        responseType: fmt === "csv" ? "blob" : "json",
      });
      const blob = fmt === "csv"
        ? resp.data
        : new Blob([JSON.stringify(resp.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `failed_videos_48h.${fmt === "csv" ? "csv" : "json"}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      const msg = e?.response?.status === 401 || e?.response?.status === 403
        ? "Not authorised. Log in as an admin first."
        : (e?.response?.data?.detail || e.message || "Download failed.");
      alert(msg);
    }
  }
  function clearFilters() {
    setFBrowser(""); setFOs(""); setFMime(""); setFCodec("");
  }
  const hasFilters = fBrowser || fOs || fMime || fCodec;

  return (
    <SectionHeader title="video rejection diagnostics" subtitle="why uploads die · last 7 days · filterable + exportable">
      <div className="flex flex-wrap items-center gap-2 mb-px text-xs" data-testid="uf-controls">
        <div className="flex gap-1">
          {[24, 168, 720].map((h) => (
            <button key={h} onClick={() => setHours(h)}
              className={`px-2 py-1 rounded border ${h === hours
                ? "bg-neutral-800 border-neutral-700 text-neutral-100"
                : "bg-neutral-950 border-neutral-900 text-neutral-500 hover:text-neutral-300"}`}
              data-testid={`uf-window-${h}`}>
              {h === 24 ? "24h" : h === 168 ? "7d" : "30d"}
            </button>
          ))}
        </div>
        <span className="text-neutral-700">·</span>
        <FilterPick label="browser" value={fBrowser} options={(data?.by_browser || []).map(b => b.key)} onChange={setFBrowser} testId="uf-filter-browser" />
        <FilterPick label="os"      value={fOs}      options={(data?.by_os || []).map(b => b.key)}      onChange={setFOs}      testId="uf-filter-os" />
        <FilterPick label="mime"    value={fMime}    options={(data?.by_mime || []).map(b => b.key)}    onChange={setFMime}    testId="uf-filter-mime" />
        <FilterPick label="codec"   value={fCodec}   options={(data?.by_codec || []).map(b => b.key)}   onChange={setFCodec}   testId="uf-filter-codec" />
        {hasFilters && (
          <button onClick={clearFilters} className="px-2 py-1 rounded border border-neutral-900 text-neutral-500 hover:text-neutral-300" data-testid="uf-filter-clear">
            clear
          </button>
        )}
        <span className="flex-grow" />
        <button
          onClick={() => downloadDebug("json")}
          className="px-2 py-1 rounded border border-amber-900/60 bg-amber-950/30 text-amber-200 hover:bg-amber-900/40"
          data-testid="uf-debug-json"
          title="Last 48h failed videos · full row-level debug data · JSON"
        >
          ⤓ failed 48h (JSON)
        </button>
        <button
          onClick={() => downloadDebug("csv")}
          className="px-2 py-1 rounded border border-amber-900/60 bg-amber-950/30 text-amber-200 hover:bg-amber-900/40"
          data-testid="uf-debug-csv"
          title="Last 48h failed videos · full row-level debug data · CSV"
        >
          ⤓ failed 48h (CSV)
        </button>
        <button onClick={exportCsv} className="px-2 py-1 rounded border border-emerald-900/60 bg-emerald-950/30 text-emerald-300 hover:bg-emerald-900/40" data-testid="uf-export-csv">
          export CSV
        </button>
      </div>
      {err && <div className="surface-1 p-4 text-red-400 text-sm" data-testid="uf-err">Error: {err}</div>}
      {!data && !err && <div className="surface-1 p-4 text-neutral-500 text-sm">Loading…</div>}
      {data && (
        <>
          <Grid cols="4">
            <Tile k="total uploads"     v={data.total_uploads}                       id="uf-total-uploads" />
            <Tile k="total failures"    v={data.total_failures}                      id="uf-total" />
            <Tile k="rejection rate"    v={`${data.rejection_rate_pct}%`}            id="uf-rejection-rate" />
            <Tile k="reasons tracked"   v={data.by_reason.length}                    id="uf-reasons-n" />
          </Grid>
          <div className="grid md:grid-cols-3 gap-px mt-px"
               style={{ background: "var(--border-default)" }}
               data-testid="uf-buckets">
            <BucketList title="by reason"     rows={data.by_reason}            testId="uf-by-reason" />
            <BucketList title="by browser"    rows={data.by_browser}           testId="uf-by-browser" />
            <BucketList title="by OS"         rows={data.by_os}                testId="uf-by-os" />
            <BucketList title="by MIME type"  rows={data.by_mime}              testId="uf-by-mime" />
            <BucketList title="by codec"      rows={data.by_codec}             testId="uf-by-codec" />
            <BucketList title="by device"     rows={data.by_device}            testId="uf-by-device" />
            <BucketList title="by file size"  rows={data.by_size_bucket}       testId="uf-by-size" />
            <BucketList title="by duration"   rows={data.by_duration_bucket}   testId="uf-by-duration" />
            <BucketList title="top Gemini errors" rows={data.by_gemini_error}  testId="uf-by-gemini-error" />
          </div>
          <div className="surface-1 mt-px p-4" data-testid="uf-recent">
            <p className="label-eyebrow mb-3">recent failures</p>
            {data.recent.length === 0 ? (
              <p className="text-sm text-neutral-500">No upload failures in this window. 🎉</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead><tr className="text-neutral-500">
                    <th className="text-left font-normal pb-1.5">when</th>
                    <th className="text-left font-normal pb-1.5">reason</th>
                    <th className="text-left font-normal pb-1.5">browser/os</th>
                    <th className="text-left font-normal pb-1.5">mime/codec</th>
                    <th className="text-left font-normal pb-1.5">size</th>
                    <th className="text-left font-normal pb-1.5">duration</th>
                    <th className="text-left font-normal pb-1.5">gemini error</th>
                  </tr></thead>
                  <tbody>
                    {data.recent.map((r) => (
                      <tr key={r.video_analysis_id} className="border-t border-neutral-900"
                          data-testid={`uf-row-${r.video_analysis_id}`}>
                        <td className="py-1.5 text-neutral-400 tabular-nums whitespace-nowrap">{shortTime(r.created_at)}</td>
                        <td className="py-1.5"><ReasonPill reason={r.rejection_reason} /></td>
                        <td className="py-1.5 text-neutral-400">
                          {r.browser || "—"}<span className="text-neutral-600"> · </span>{r.os || "—"}
                        </td>
                        <td className="py-1.5 text-neutral-500">
                          {r.mime_type || "—"}<span className="text-neutral-700"> · </span>{r.codec || "—"}
                        </td>
                        <td className="py-1.5 text-neutral-300 tabular-nums">{fmtBytes(r.size_bytes)}</td>
                        <td className="py-1.5 text-neutral-300 tabular-nums">{r.duration_s ? `${r.duration_s}s` : "—"}</td>
                        <td className="py-1.5 text-neutral-500 max-w-xs truncate" title={r.gemini_error || ""}>
                          {r.gemini_error ? r.gemini_error.slice(0, 80) : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </SectionHeader>
  );
}

function FilterPick({ label, value, options, onChange, testId }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="px-2 py-1 rounded bg-neutral-950 border border-neutral-900 text-neutral-300 text-xs"
      data-testid={testId}
    >
      <option value="">all {label}</option>
      {options.filter(o => o && o !== "unknown").slice(0, 12).map((o) => (
        <option key={o} value={o}>{o}</option>
      ))}
    </select>
  );
}

// ============================================================================
// Review Disputes — trust panel (P0 product reliability sprint)
function SectionReviewDisputes() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [openId, setOpenId] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get("/admin/web/review-disputes", { params: { limit: 30, hours: 720 } })
      .then(r => { if (alive) setData(r.data); })
      .catch((e) => { if (alive) setErr(e?.response?.data?.detail || "failed"); });
    return () => { alive = false; };
  }, []);
  const openRow = openId ? (data?.recent || []).find(r => r.dispute_id === openId) : null;
  return (
    <SectionHeader title="disputed reviews" subtitle="last 30 days · trust signal on Gully's reviews">
      {err && <div className="surface-1 p-4 text-red-400 text-sm" data-testid="rd-err">Error: {err}</div>}
      {!data && !err && <div className="surface-1 p-4 text-neutral-500 text-sm">Loading…</div>}
      {data && (
        <>
          <Grid cols="3">
            <Tile k="disputes"        v={data.total}                  id="rd-total" />
            <Tile k="distinct triggers" v={data.by_keyword.length}    id="rd-triggers" />
            <Tile k="window"          v={`${Math.round(data.window_hours/24)}d`} id="rd-window" />
          </Grid>
          {data.by_keyword.length > 0 && (
            <div className="surface-1 mt-px p-4" data-testid="rd-keywords">
              <p className="label-eyebrow mb-3">top trigger phrases</p>
              <ul className="space-y-1.5">
                {data.by_keyword.map((r) => (
                  <li key={r.key} className="flex justify-between text-sm">
                    <span className="text-neutral-300">&ldquo;{r.key}&rdquo;</span>
                    <span className="font-mono text-neutral-100">{r.count}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="surface-1 mt-px divide-y divide-neutral-900" data-testid="rd-list">
            {data.recent.length === 0 ? (
              <p className="p-4 text-sm text-neutral-500">No disputed reviews in this window.</p>
            ) : data.recent.map((r) => (
              <button
                key={r.dispute_id}
                onClick={() => setOpenId(r.dispute_id)}
                className="w-full text-left px-4 py-3 hover:bg-neutral-900/60 transition-colors"
                data-testid={`rd-row-${r.dispute_id}`}
              >
                <div className="flex items-center gap-2 text-xs text-neutral-500 mb-1">
                  <span>{shortTime(r.created_at)}</span>
                  <span className="text-neutral-700">·</span>
                  <span className="text-neutral-400">user {r.user_id || "anon"}</span>
                  <span className="text-neutral-700">·</span>
                  <span className="px-1.5 py-0.5 rounded bg-amber-900/40 text-amber-200 text-[10px]">
                    &ldquo;{r.matched_keyword}&rdquo;
                  </span>
                </div>
                <div className="text-sm text-neutral-100 line-clamp-2">{r.user_message}</div>
                {r.review_match_day_cue && (
                  <div className="text-xs text-neutral-500 mt-1 italic line-clamp-1">
                    review cue: {r.review_match_day_cue}
                  </div>
                )}
              </button>
            ))}
          </div>
          {openRow && <DisputeDrawer row={openRow} onClose={() => setOpenId(null)} />}
        </>
      )}
    </SectionHeader>
  );
}

function DisputeDrawer({ row, onClose }) {
  return (
    <div className="fixed inset-0 z-40 flex justify-end" onClick={onClose} data-testid="rd-drawer">
      <div className="absolute inset-0 bg-black/60" />
      <div className="relative w-full sm:max-w-xl h-full bg-[#0c0c0c] border-l border-neutral-800 overflow-y-auto p-5"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-start mb-4">
          <div>
            <p className="label-eyebrow">disputed review</p>
            <p className="text-xs text-neutral-500 mt-1 font-mono">{row.dispute_id}</p>
            <p className="text-xs text-neutral-500 mt-0.5">video {row.video_analysis_id?.slice(-10)}</p>
          </div>
          <button onClick={onClose} className="text-neutral-500 hover:text-white text-sm px-3 py-1 rounded border border-neutral-800"
                  data-testid="rd-drawer-close">Close</button>
        </div>
        <div className="space-y-4">
          <div className="surface-1 p-3">
            <p className="label-eyebrow mb-1">user said · {shortTime(row.created_at)}</p>
            <p className="text-sm text-neutral-100 whitespace-pre-wrap">{row.user_message}</p>
            <p className="text-xs text-amber-300/80 mt-2">matched: &ldquo;{row.matched_keyword}&rdquo;</p>
          </div>
          <div className="surface-1 p-3">
            <p className="label-eyebrow mb-1">review that was disputed · {shortTime(row.review_created_at)}</p>
            {row.review_match_day_cue && (
              <p className="text-sm text-neutral-200 mb-2 italic">cue: {row.review_match_day_cue}</p>
            )}
            {row.review_observations.length > 0 ? (
              <ul className="list-disc pl-5 space-y-1 text-sm text-neutral-300">
                {row.review_observations.map((o, i) => <li key={i}>{o}</li>)}
              </ul>
            ) : (
              <p className="text-xs text-neutral-500">No observations on the review payload.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function BucketList({ title, rows, testId }) {
  return (
    <div className="surface-1 p-4" data-testid={testId}>
      <p className="label-eyebrow mb-3">{title}</p>
      {rows.length === 0 ? (
        <p className="text-sm text-neutral-500">No data yet.</p>
      ) : (
        <ul className="space-y-1.5">
          {rows.slice(0, 8).map((r) => (
            <li key={r.key} className="flex justify-between text-sm">
              <span className="text-neutral-300 truncate pr-2">{r.key.replace(/_/g, " ")}</span>
              <span className="font-mono text-neutral-100 tabular-nums">{r.count}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ReasonPill({ reason }) {
  const r = (reason || "UNKNOWN").toUpperCase();
  // Color buckets by recoverability — user-fixable (yellow), our problem (red), unknown (grey).
  const userFixable = new Set(["FILE_TOO_LARGE", "VIDEO_TOO_LONG", "UNSUPPORTED_FORMAT", "NETWORK_ERROR", "UPLOAD_TIMEOUT"]);
  const serverSide  = new Set(["GEMINI_REJECTED", "SERVER_ERROR", "PROCESSING_TIMEOUT"]);
  let cls = "bg-neutral-900 border-neutral-800 text-neutral-300";
  if (userFixable.has(r)) cls = "bg-amber-950/60 border-amber-900/60 text-amber-200";
  else if (serverSide.has(r)) cls = "bg-red-950/60 border-red-900/60 text-red-200";
  return (
    <span className={`px-1.5 py-0.5 rounded border text-[10px] uppercase tracking-wide ${cls}`}>
      {r.replace(/_/g, " ")}
    </span>
  );
}

function fmtBytes(n) {
  if (n == null) return "—";
  if (n < 1024) return `${n}B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)}KB`;
  return `${(n / (1024 * 1024)).toFixed(1)}MB`;
}

// ============================================================================
// Section 4 — Revenue
function Section4Revenue({ rev }) {
  return (
    <SectionHeader title="revenue" subtitle="paywall to MRR · live mode only">
      <Grid cols="4">
        <Tile k="paywall shown"      v={rev.paywall_shown_24h}      id="s4-shown" />
        <Tile k="paywall engaged"    v={rev.paywall_engaged_24h}    id="s4-engaged" />
        <Tile k="paywall dismissed"  v={rev.paywall_dismissed_24h}  id="s4-dismissed" />
        <Tile k="upgrade clicks"     v={rev.upgrade_clicks_24h}     id="s4-clicks" />
        <Tile k="checkout starts"    v={rev.checkout_starts_24h}    id="s4-co" />
        <Tile k="payments OK"        v={rev.payments_completed_24h} id="s4-pay-ok" />
        <Tile k="payments failed"    v={rev.payments_failed_24h}    id="s4-pay-fail" />
        <Tile k="active subscriptions" v={rev.active_subscriptions} id="s4-subs" />
        <Tile k="MRR"                v={fmtMRR(rev.mrr_amount_minor, rev.mrr_currency)} id="s4-mrr" />
      </Grid>
      {!rev.mrr_instrumented && (
        <p className="mt-3 text-xs text-amber-400/80">
          MRR will populate once Stripe webhook lands canonical <code>plus_amount_monthly</code> on each user.
        </p>
      )}
    </SectionHeader>
  );
}

// ============================================================================
// Cue Lifecycle (Phase B3) — founder-internal observability for the
// active→improving→resolved→archived flow. No user-facing surface — this
// tile exists so the founder can see whether the lifecycle regex/threshold
// logic is firing at the right rate.
function SectionCueLifecycle({ counts, windowLabel }) {
  // counts is always present in the response (safe-wrapper in server.py).
  const improving = counts?.improving ?? 0;
  const resolved  = counts?.resolved  ?? 0;
  const archived  = counts?.archived  ?? 0;
  const total = improving + resolved + archived;
  return (
    <SectionHeader
      title="cue lifecycle"
      subtitle={`internal · how the memory engine is moving cues · ${windowLabel || "last 90 days"}`}
    >
      <Grid cols="4">
        <Tile k="cues improving" v={improving} id="cl-improving" />
        <Tile k="cues resolved"  v={resolved}  id="cl-resolved" />
        <Tile k="cues archived"  v={archived}  id="cl-archived" />
        <Tile k="total transitions" v={total}  id="cl-total" />
      </Grid>
      <p className="mt-3 text-xs text-neutral-500" data-testid="cl-note">
        Signal-only. Internal observability for the cue lifecycle regex + thresholds.
        Sourced from <code>audit_events.kind = &quot;cue_transition&quot;</code>.
        No raw chat text is stored — payload is shape-only.
      </p>
    </SectionHeader>
  );
}

// ============================================================================
// Phase B5 — Weekly Reflections. Soft continuity hook surfaced on the first
// chat turn of a new ISO week. Founder-internal: track generation volume,
// how many actually surfaced (thin-signal weeks generate but don't surface),
// and the returning-user funnel that the reflection is trying to lift.
function SectionWeeklyReflections({ wr, windowLabel }) {
  const generated = wr?.generated ?? 0;
  const surfaced = wr?.surfaced ?? 0;
  const meaningful = wr?.meaningful ?? 0;
  const thin = wr?.thin_signal ?? 0;
  const ret7 = wr?.returning_7d ?? 0;
  const ret30 = wr?.returning_30d ?? 0;
  const surfaceRate = meaningful > 0
    ? Math.round((surfaced / meaningful) * 1000) / 10
    : null;
  return (
    <SectionHeader
      title="weekly reflections"
      subtitle={`internal · continuity nudges on first turn of a new ISO week · ${windowLabel || "last 90 days"}`}
    >
      <Grid cols="4">
        <Tile k="reflections generated" v={generated} id="wr-generated" />
        <Tile k="reflections surfaced" v={surfaced} id="wr-surfaced" />
        <Tile k="returning users · 7d" v={ret7} id="wr-returning-7d" />
        <Tile k="returning users · 30d" v={ret30} id="wr-returning-30d" />
      </Grid>
      <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs text-neutral-500" data-testid="wr-secondary">
        <div data-testid="wr-meaningful">meaningful: <span className="text-neutral-300">{meaningful}</span></div>
        <div data-testid="wr-thin-signal">thin signal (stored, not surfaced): <span className="text-neutral-300">{thin}</span></div>
        <div data-testid="wr-surface-rate">surface rate: <span className="text-neutral-300">{surfaceRate == null ? "—" : `${surfaceRate}%`}</span></div>
      </div>
      <p className="mt-3 text-xs text-neutral-500" data-testid="wr-note">
        Deterministic templated text. No LLM cost. Thin-signal weeks generate a
        stored doc for analytics but never surface to the user. Sourced from
        <code> weekly_reflections</code> + <code>web_messages</code>. Returning =
        user-messages in window <em>and</em> in the prior window of the same size.
      </p>
    </SectionHeader>
  );
}

// ============================================================================
// Founder Observation Plan (2026-02-25). Measure-before-build. Single
// section so the founder sees ALL 10 observation metrics in one glance,
// pulled from existing tiles (`weekly_reflections`, `video_health`) plus
// a small `continuity_observation` block. No new features built — just
// instrumentation to decide whether Phase B5 is moving the needle.
function SectionContinuityObservation({ obs, wr, videoHealth, windowLabel }) {
  const ret7 = wr?.returning_7d ?? 0;
  const ret30 = wr?.returning_30d ?? 0;
  const generated = wr?.generated ?? 0;
  const surfaced = wr?.surfaced ?? 0;
  const viewed = wr?.viewed ?? 0;
  const meaningful = wr?.meaningful ?? 0;
  const thin = wr?.thin_signal ?? 0;
  const surfaceRate = meaningful > 0
    ? Math.round((surfaced / meaningful) * 1000) / 10
    : null;
  const viewRate = surfaced > 0
    ? Math.round((viewed / surfaced) * 1000) / 10
    : null;
  const sessionsPerUser = obs?.sessions_per_user;
  const repeatUsers = obs?.repeat_conversation_users ?? 0;
  const linkedUsers = obs?.linked_users_in_window ?? 0;
  const distinctSessions = obs?.distinct_sessions_in_window ?? 0;
  const billingFP = obs?.billing_false_positives_window ?? 0;
  const repeatPct = linkedUsers > 0
    ? Math.round((repeatUsers / linkedUsers) * 1000) / 10
    : null;
  const reviewSuccessPct = videoHealth?.review_success_rate_pct;
  const reviewRejectionPct = (reviewSuccessPct == null)
    ? null
    : Math.round((100 - reviewSuccessPct) * 10) / 10;
  return (
    <SectionHeader
      title="continuity observation"
      subtitle={`founder observation plan · 7-14 day measure-before-build · ${windowLabel || "last 90 days"}`}
    >
      <Grid cols="4">
        <Tile k="returning users · 7d"  v={ret7}             id="obs-ret-7d" />
        <Tile k="returning users · 30d" v={ret30}            id="obs-ret-30d" />
        <Tile k="sessions / user"       v={sessionsPerUser == null ? "—" : sessionsPerUser} id="obs-sessions-per-user" />
        <Tile k="repeat conv users"     v={repeatUsers}      id="obs-repeat-users" />
      </Grid>
      <Grid cols="4">
        <Tile k="msgs / user"
              v={obs?.messages_per_user == null ? "—" : obs.messages_per_user}
              id="obs-msgs-per-user" />
        <Tile k="msgs / session"
              v={obs?.messages_per_session == null ? "—" : obs.messages_per_session}
              id="obs-msgs-per-session" />
        <Tile k="median conv length"
              v={obs?.median_conversation_length ?? 0}
              id="obs-median-conv" />
        <Tile k="returning conv depth"
              v={obs?.returning_user_conversation_depth == null
                   ? "—" : obs.returning_user_conversation_depth}
              id="obs-returning-depth" />
      </Grid>
      <Grid cols="4">
        <Tile k="conv starts / visit"
              v={obs?.conversation_starts_per_visit_pct == null
                   ? "—" : `${obs.conversation_starts_per_visit_pct}%`}
              id="obs-starts-per-visit" />
        <Tile k="starter-chip starts"
              v={obs?.starter_chip_starts_in_window ?? 0}
              id="obs-chip-starts" />
        <Tile k="chip share of starts"
              v={obs?.starter_chip_share_of_starts_pct == null
                   ? "—" : `${obs.starter_chip_share_of_starts_pct}%`}
              id="obs-chip-share" />
        <Tile k="distinct sessions in window"
              v={distinctSessions} id="obs-distinct-sessions-tile" />
      </Grid>
      <Grid cols="4">
        <Tile k="reflections generated" v={generated}        id="obs-wr-generated" />
        <Tile k="reflections surfaced"  v={surfaced}         id="obs-wr-surfaced" />
        <Tile k="reflections viewed"    v={viewed}           id="obs-wr-viewed" />
        <Tile k="surface → view rate"   v={viewRate == null ? "—" : `${viewRate}%`} id="obs-wr-view-rate" />
      </Grid>
      <Grid cols="4">
        <Tile k="video review success"  v={reviewSuccessPct == null ? "—" : `${reviewSuccessPct}%`} id="obs-video-success" />
        <Tile k="video review reject"   v={reviewRejectionPct == null ? "—" : `${reviewRejectionPct}%`} id="obs-video-reject" />
        <Tile k="billing false positives" v={billingFP}      id="obs-billing-fp" />
        <Tile k="meaningful → surface"  v={surfaceRate == null ? "—" : `${surfaceRate}%`} id="obs-wr-surface-rate" />
      </Grid>
      <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs text-neutral-500" data-testid="obs-secondary">
        <div data-testid="obs-linked-users">linked users in window: <span className="text-neutral-300">{linkedUsers}</span></div>
        <div data-testid="obs-distinct-sessions">distinct sessions: <span className="text-neutral-300">{distinctSessions}</span></div>
        <div data-testid="obs-repeat-pct">repeat conv share: <span className="text-neutral-300">{repeatPct == null ? "—" : `${repeatPct}%`}</span></div>
        <div data-testid="obs-thin-signal">thin signal (stored, not surfaced): <span className="text-neutral-300">{thin}</span></div>
      </div>
      <p className="mt-3 text-xs text-neutral-500" data-testid="obs-note">
        Observation plan — 10 metrics in one panel so we can decide whether to
        invest further in continuity. Targets + decision criteria in
        <code> /app/memory/B5_OBSERVATION_PLAN.md</code>. <em>Viewed</em> =
        the user sent ≥1 follow-up message in the same session after the
        reflection was surfaced (best-available proxy without a frontend
        view-ping). <em>Repeat conv user</em> = linked user with ≥2 distinct
        sessions in window.
      </p>
    </SectionHeader>
  );
}

// ============================================================================
// Section 5 — Conversations
function Section5Conversations() {
  const [filter, setFilter] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openSid, setOpenSid] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get("/admin/web/conversations", { params: { filter, limit: 50, offset: 0 } })
      .then(r => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData({ rows: [], plus_count: 0, unread_count: 0 }); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [filter]);

  // Iter XX.18 — new Plus + Unread quick filters, ordered ahead of the
  // funnel-specific filters so they're immediately visible to the founder.
  const filters = [
    ["all", "All"],
    ["plus", "⭐ Plus"],
    ["unread", "Unread"],
    ["new", "New users"],
    ["returning", "Returning"],
    ["video", "Video users"],
    ["failed_reviews", "Failed reviews"],
    ["successful_reviews", "Successful reviews"],
    ["paywall_hit", "Hit paywall"],
    ["checkout_started", "Checkout started"],
    ["paid", "Paid"],
  ];

  // Iter XX.18 — client-side search: never leaves the currently loaded
  // page but preserves the plus/unread ordering. Matches name / phone /
  // email / preview / session_id substring, case-insensitive.
  const rowsAll = data?.rows || [];
  const q = search.trim().toLowerCase();
  const rows = q
    ? rowsAll.filter((r) => (
        (r.name || "").toLowerCase().includes(q) ||
        (r.phone_masked || "").toLowerCase().includes(q) ||
        (r.email_masked || "").toLowerCase().includes(q) ||
        (r.preview_user || "").toLowerCase().includes(q) ||
        (r.preview_assistant || "").toLowerCase().includes(q) ||
        (r.session_id || "").toLowerCase().includes(q)
      ))
    : rowsAll;

  // Iter XX.18 — split into Plus + Everyone-else buckets so we can render
  // a section header between them (only when filter is "all" and there
  // are Plus rows to show).
  const plusRows = rows.filter((r) => r.is_plus);
  const otherRows = rows.filter((r) => !r.is_plus);
  const showSectionHeaders = filter === "all" && plusRows.length > 0 && otherRows.length > 0;

  const subtitle = data
    ? `founder reading view · ${data.plus_count ?? 0} on plus · ${data.unread_count ?? 0} unread`
    : "founder reading view · web only";

  return (
    <SectionHeader title="conversations" subtitle={subtitle}>
      <div className="flex flex-wrap items-center gap-1.5 mb-3" data-testid="s5-filters">
        {filters.map(([id, label]) => (
          <button
            key={id}
            onClick={() => setFilter(id)}
            className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
              filter === id
                ? "bg-emerald-600/80 border-emerald-500 text-white"
                : "bg-neutral-900 border-neutral-800 text-neutral-300 hover:border-neutral-700"
            }`}
            data-testid={`s5-filter-${id}`}
          >
            {label}
          </button>
        ))}
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name / phone / preview"
          className="ml-auto bg-neutral-950 border border-neutral-800 rounded-full px-3 py-1.5 text-xs min-w-[220px]"
          data-testid="s5-search-input"
        />
      </div>
      {loading && <div className="p-4 text-neutral-500 text-sm">Loading conversations…</div>}
      {!loading && rows.length === 0 && (
        <div className="surface-1 p-6 text-neutral-500 text-sm" data-testid="s5-empty">
          No conversations match this filter.
        </div>
      )}
      {!loading && rows.length > 0 && (
        <div className="space-y-4" data-testid="s5-list">
          {showSectionHeaders && (
            <>
              <ConversationSectionHeader
                label={`⭐ Gully Plus · ${plusRows.length}`}
                testid="s5-section-plus"
              />
              <div className="surface-1 divide-y divide-neutral-900">
                {plusRows.map((r) => (
                  <ConversationRow key={r.session_id} row={r} onOpen={() => setOpenSid(r.session_id)} />
                ))}
              </div>
              <ConversationSectionHeader
                label="Everyone else"
                testid="s5-section-everyone"
              />
              <div className="surface-1 divide-y divide-neutral-900">
                {otherRows.map((r) => (
                  <ConversationRow key={r.session_id} row={r} onOpen={() => setOpenSid(r.session_id)} />
                ))}
              </div>
            </>
          )}
          {!showSectionHeaders && (
            <div className="surface-1 divide-y divide-neutral-900">
              {rows.map((r) => (
                <ConversationRow key={r.session_id} row={r} onOpen={() => setOpenSid(r.session_id)} />
              ))}
            </div>
          )}
        </div>
      )}
      {openSid && <ConversationDrawer sessionId={openSid} onClose={() => setOpenSid(null)} />}
    </SectionHeader>
  );
}

function ConversationSectionHeader({ label, testid }) {
  return (
    <div
      className="text-[11px] uppercase tracking-widest text-neutral-500 px-1"
      data-testid={testid}
    >
      {label}
    </div>
  );
}

function ConversationRow({ row, onOpen }) {
  return (
    <button
      onClick={onOpen}
      className={`w-full text-left px-4 py-3 hover:bg-neutral-900/60 transition-colors flex gap-4 items-start ${
        row.is_plus ? "border-l-2 border-l-amber-400/70" : ""
      }`}
      data-testid={`s5-row-${row.session_id}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 text-xs text-neutral-500 mb-1 flex-wrap">
          <span className="text-neutral-300 truncate max-w-[140px]">
            {row.name || row.phone_masked || row.email_masked || "anonymous"}
          </span>
          {row.is_plus && (
            <span
              className="px-1.5 py-0.5 rounded bg-amber-900/40 text-amber-200 border border-amber-800 text-[10px] uppercase tracking-wide flex items-center gap-1"
              data-testid={`s5-plus-badge-${row.session_id}`}
              title="Active Gully Plus subscription"
            >
              <span aria-hidden>⭐</span> Gully Plus
            </span>
          )}
          {row.is_unread && (
            <span
              className="px-1.5 py-0.5 rounded bg-emerald-900/40 text-emerald-200 border border-emerald-800 text-[10px] uppercase tracking-wide"
              data-testid={`s5-unread-badge-${row.session_id}`}
              title="Last message is from the user"
            >
              Unread
            </span>
          )}
          <span className="text-neutral-700">·</span>
          <span className="tabular-nums">{shortTime(row.last_active_at)}</span>
          {row.billing !== "free" && !row.is_plus && (
            <span className="ml-1 px-1.5 py-0.5 rounded bg-emerald-900/50 text-emerald-200 text-[10px] uppercase">
              {row.billing}
            </span>
          )}
        </div>
        <div className="text-sm text-neutral-200 truncate">
          {row.preview_user || <span className="text-neutral-600 italic">no messages yet</span>}
        </div>
        {row.preview_assistant && (
          <div className="text-xs text-neutral-500 mt-0.5 truncate">
            → {row.preview_assistant}
          </div>
        )}
      </div>
      <div className="flex-none text-right text-xs text-neutral-500 space-y-0.5 tabular-nums">
        <div>{row.user_msg_count}u · {row.assistant_msg_count}a</div>
        {row.video_upload_count > 0 && (
          <div className="text-emerald-400/80">
            🎥 {row.video_upload_count} ({row.review_success_count}✓ {row.review_failure_count}✗)
          </div>
        )}
      </div>
    </button>
  );
}

function ConversationDrawer({ sessionId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get(`/admin/web/conversations/${encodeURIComponent(sessionId)}`)
      .then(r => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [sessionId]);

  return (
    <div className="fixed inset-0 z-40 flex justify-end" onClick={onClose} data-testid="s5-drawer">
      <div className="absolute inset-0 bg-black/60" />
      <div
        className="relative w-full sm:max-w-2xl h-full bg-[#0c0c0c] border-l border-neutral-800 overflow-y-auto p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-start mb-4">
          <div>
            <p className="label-eyebrow">conversation timeline</p>
            <p className="text-xs text-neutral-500 mt-1 font-mono">{sessionId}</p>
            {data?.user && (
              <p className="text-sm text-neutral-300 mt-2 flex items-center gap-2 flex-wrap">
                <span>{data.user.name || data.user.phone_number || "linked"}</span>
                {data?.is_plus && (
                  <span
                    className="px-1.5 py-0.5 rounded bg-amber-900/40 text-amber-200 border border-amber-800 text-[10px] uppercase tracking-wide flex items-center gap-1"
                    data-testid="s5-drawer-plus-badge"
                    title="Active Gully Plus subscription"
                  >
                    <span aria-hidden>⭐</span> Gully Plus
                  </span>
                )}
                {!data?.is_plus && data.user.plus_active && (
                  <span className="px-1.5 py-0.5 rounded bg-emerald-900/50 text-emerald-200 text-[10px] uppercase">PLUS</span>
                )}
              </p>
            )}
          </div>
          <button onClick={onClose}
                  className="text-neutral-500 hover:text-white text-sm px-3 py-1 rounded border border-neutral-800"
                  data-testid="s5-drawer-close">Close</button>
        </div>
        {loading && <div className="text-neutral-500 text-sm">Loading timeline…</div>}
        {!loading && (!data || data.event_count === 0) && (
          <div className="text-neutral-500 text-sm">No timeline events.</div>
        )}
        {!loading && data && (
          <div className="space-y-2">
            {data.timeline.map((ev, i) => (
              <TimelineEvent key={i} ev={ev} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function TimelineEvent({ ev }) {
  const k = ev.kind || "";
  const isUserMsg = k === "message:user";
  const isAssistant = k === "message:assistant";
  const isVideo = k.startsWith("video:");
  const isEvent = k.startsWith("event:");
  if (isUserMsg) return (
    <div className="flex justify-end">
      <div className="max-w-[80%] rounded-2xl px-3 py-2 bg-emerald-600/85 text-white text-sm whitespace-pre-wrap">
        {ev.content}
        <div className="text-[10px] text-emerald-100/70 mt-1">{shortTime(ev.at)}</div>
      </div>
    </div>
  );
  if (isAssistant) return (
    <div className="flex justify-start">
      <div className="max-w-[80%] rounded-2xl px-3 py-2 bg-neutral-900 border border-neutral-800 text-sm text-neutral-100 whitespace-pre-wrap">
        {ev.content}
        <div className="text-[10px] text-neutral-500 mt-1">{shortTime(ev.at)}</div>
      </div>
    </div>
  );
  if (isVideo) {
    const ok = k === "video:success";
    return (
      <div className={`text-xs px-3 py-2 rounded-lg ${ok ? "bg-emerald-950/40 border border-emerald-900/40 text-emerald-200" : "bg-red-950/30 border border-red-900/40 text-red-200"}`}>
        🎥 video {ok ? "review succeeded" : "review rejected"}
        {ev.rejection_reason && <span className="ml-2 italic">— {ev.rejection_reason}</span>}
        <span className="ml-2 text-[10px] opacity-60">{shortTime(ev.at)}</span>
      </div>
    );
  }
  if (isEvent) {
    return (
      <div className="text-[11px] px-3 py-1.5 rounded bg-neutral-950 border border-neutral-900 text-neutral-400">
        <span className="text-neutral-300">{k.replace("event:", "").replace(/_/g, " ")}</span>
        {ev.info && <span className="ml-2">· {ev.info}</span>}
        <span className="ml-2 opacity-60">{shortTime(ev.at)}</span>
      </div>
    );
  }
  return null;
}

// ============================================================================
// Section 6 — Intelligence
function Section6Intelligence({ intel }) {
  return (
    <SectionHeader title="user intelligence" subtitle={`web messages · sample size ${intel.sample_size}`}>
      {!intel.enough_data ? (
        <div className="surface-1 p-6 text-neutral-400 text-sm" data-testid="s6-empty">
          Not enough web data yet. Need ≥{intel.min_required} user messages to surface reliable themes. Currently {intel.sample_size}.
        </div>
      ) : (
        <Grid cols="3">
          {intel.topics.slice(0, 9).map(t => (
            <Tile key={t.topic} k={t.topic} v={t.count} id={`s6-topic-${t.topic}`} />
          ))}
        </Grid>
      )}
    </SectionHeader>
  );
}

// ============================================================================
// Section 7 — Power Users
function Section7PowerUsers() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/web/power-users", { params: { limit: 10 } })
      .then(r => setData(r.data))
      .catch(() => setData(null));
  }, []);
  if (!data) return (
    <SectionHeader title="power users" subtitle="top web users by activity">
      <div className="p-4 text-neutral-500 text-sm">Loading…</div>
    </SectionHeader>
  );
  return (
    <SectionHeader title="power users" subtitle="top web users by activity">
      <div className="grid md:grid-cols-2 gap-4">
        <PowerTable title="by uploads" rows={data.by_uploads}
                    cols={[["user_id","user"],["uploads","up"],["review_success","ok"],["billing","b"]]}
                    testId="s7-uploads" />
        <PowerTable title="by messages" rows={data.by_messages}
                    cols={[["user_id","user"],["messages","msg"],["billing","b"]]}
                    testId="s7-messages" />
        <PowerTable title="by repeat sessions" rows={data.by_sessions}
                    cols={[["user_id","user"],["sessions","sess"],["billing","b"]]}
                    testId="s7-sessions" />
        <PowerTable title={`closest to paywall (free limit ${data.free_limit_assumed})`} rows={data.closest_to_paywall}
                    cols={[["user_id","user"],["uploads","up"],["billing","b"]]}
                    testId="s7-close" emptyText="No users one upload away yet." />
      </div>
      <div className="mt-4">
        <PowerTable title="hit paywall but didn't upgrade" rows={data.paywall_no_upgrade}
                    cols={[["user_id","user"],["paywall_views","views"]]}
                    testId="s7-stuck"
                    emptyText="No users in this state." />
      </div>
    </SectionHeader>
  );
}

function PowerTable({ title, rows, cols, testId, emptyText }) {
  return (
    <div className="surface-1 p-4" data-testid={testId}>
      <p className="label-eyebrow mb-3">{title}</p>
      {(!rows || rows.length === 0) ? (
        <p className="text-sm text-neutral-500">{emptyText || "Nothing here yet."}</p>
      ) : (
        <table className="w-full text-sm">
          <thead><tr className="text-neutral-500 text-xs">
            {cols.map(([_, lbl]) => <th key={lbl} className="text-left font-normal pb-1.5">{lbl}</th>)}
          </tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-neutral-900">
                {cols.map(([key]) => (
                  <td key={key} className="py-1.5 text-neutral-200 tabular-nums">{r[key] ?? "—"}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ============================================================================
// Shared atoms
function SectionHeader({ title, subtitle, children }) {
  return (
    <section data-testid={`section-${title.replace(/\s+/g, "-")}`}>
      <div className="mb-4">
        <p className="label-eyebrow">{title}</p>
        {subtitle && <p className="text-xs text-neutral-500 mt-0.5">{subtitle}</p>}
      </div>
      {children}
    </section>
  );
}

function Surface({ children }) {
  return <div className="surface-1">{children}</div>;
}

function Grid({ cols = "4", children }) {
  const grid = { "3": "grid-cols-2 md:grid-cols-3", "4": "grid-cols-2 md:grid-cols-4",
                  "6": "grid-cols-2 md:grid-cols-3 lg:grid-cols-6" }[cols] || "grid-cols-2 md:grid-cols-4";
  return <div className={`grid ${grid} gap-px`} style={{ background: "var(--border-default)" }}>{children}</div>;
}

function Tile({ k, v, id, sub }) {
  return (
    <div className="surface-1 p-4" data-testid={`tile-${id}`}>
      <p className="label-eyebrow mb-1">{k}</p>
      <p className="font-display text-2xl font-light tabular-nums">{v ?? "—"}</p>
      {sub != null && sub !== "" && (
        <p className="mt-1 text-[11px] leading-snug text-neutral-500">{sub}</p>
      )}
    </div>
  );
}

// Helpers
function pct(v) { return v == null ? "—" : `${v}%`; }
function fmtMs(ms) {
  if (ms == null) return "—";
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}
function fmtMRR(amountMinor, currency) {
  if (!amountMinor) return "—";
  // Minor units (paise / cents). INR default.
  const sym = currency === "INR" ? "₹" : currency === "USD" ? "$" : "";
  return `${sym}${(amountMinor / 100).toFixed(0)}`;
}
function shortTime(v) {
  if (!v) return "";
  const d = typeof v === "string" ? new Date(v) : v;
  if (!(d instanceof Date) || isNaN(d)) return "";
  const now = new Date();
  const diff = (now - d) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return d.toLocaleDateString();
}


// ---------- Iter XX.4 — Gully Plus Launch Analytics ----------
// Reads /api/admin/plus/analytics and renders the launch funnel:
// banner shown → banner dismissed / clicked → payment page → subscribed.
function SectionPlusLaunchAnalytics({ sinceDays }) {
  const [snap, setSnap] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    async function load() {
      try {
        const { data } = await api.get("/admin/plus/analytics", {
          params: { since_days: sinceDays },
        });
        if (alive) { setSnap(data); setErr(null); }
      } catch (e) {
        if (alive) setErr(e?.response?.data?.detail || "Could not load Plus analytics.");
      }
    }
    load();
    const t = setInterval(load, 60000);
    return () => { alive = false; clearInterval(t); };
  }, [sinceDays]);

  return (
    <Surface>
      <div className="p-6" data-testid="plus-launch-analytics">
        <h2 className="text-sm font-medium mb-1">Plus Launch (Iter XX.4)</h2>
        <p className="text-xs text-neutral-500 mb-4">
          Banner → click → payment page → subscribed. Window: {sinceDays}d.
        </p>
        {err && (
          <div className="text-red-400 text-sm" data-testid="plus-launch-analytics-error">
            {err}
          </div>
        )}
        {!err && !snap && <div className="text-neutral-500 text-sm">Loading…</div>}
        {snap && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Metric
                label="Banner shown"
                value={snap.overall_funnel?.banner_shown ?? 0}
                testid="plus-metric-banner-shown"
              />
              <Metric
                label="Upgrade clicked"
                value={snap.overall_funnel?.upgrade_clicked ?? 0}
                testid="plus-metric-upgrade-clicked"
              />
              <Metric
                label="Payment page"
                value={snap.overall_funnel?.payment_page_opened ?? 0}
                testid="plus-metric-payment-page"
              />
              <Metric
                label="Subscribed"
                value={snap.overall_funnel?.subscribed ?? 0}
                testid="plus-metric-subscribed"
              />
            </div>
            <div className="grid grid-cols-3 gap-3 pt-3 border-t border-neutral-800">
              <Metric
                label="Banner CTR"
                value={`${(snap.banner_ctr ?? 0).toFixed(1)}%`}
                testid="plus-metric-banner-ctr"
              />
              <Metric
                label="Click → payment"
                value={`${(snap.click_to_payment_page ?? 0).toFixed(1)}%`}
                testid="plus-metric-click-to-payment"
              />
              <Metric
                label="Payment → sub"
                value={`${(snap.payment_page_conversion ?? 0).toFixed(1)}%`}
                testid="plus-metric-payment-conversion"
              />
            </div>
            <details className="text-xs text-neutral-500 pt-2">
              <summary className="cursor-pointer hover:text-neutral-300">
                Raw event counts
              </summary>
              <pre className="mt-2 text-neutral-500 whitespace-pre-wrap">
                {JSON.stringify(snap.counts, null, 2)}
              </pre>
            </details>
          </div>
        )}
      </div>
    </Surface>
  );
}

function Metric({ label, value, testid }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-neutral-500 mb-1">{label}</p>
      <p className="text-2xl font-light text-neutral-100" data-testid={testid}>
        {value}
      </p>
    </div>
  );
}


// ---------- Iter XX.10 — Abandoned Gully Plus ----------
// Every checkout is a conversation. Lists players who got close to
// subscribing but didn't complete within 24h, with copy-friendly
// identifiers and persisted follow-up state (contacted / high priority
// / free-text notes).
const ABANDONED_WINDOWS = [
  { id: "today", label: "Today" },
  { id: "7d", label: "Last 7 days" },
  { id: "30d", label: "Last 30 days" },
];
const ABANDONED_FILTERS = [
  { id: "all", label: "All" },
  { id: "never_contacted", label: "Never contacted" },
  { id: "contacted", label: "Contacted" },
  { id: "high_priority", label: "High priority" },
];

function fmtSecs(s) {
  if (s == null) return "—";
  const sec = Number(s);
  if (!Number.isFinite(sec) || sec < 0) return "—";
  if (sec < 60) return `${Math.round(sec)}s`;
  if (sec < 3600) return `${Math.round(sec / 60)}m`;
  const h = Math.floor(sec / 3600);
  const m = Math.round((sec % 3600) / 60);
  return m ? `${h}h ${m}m` : `${h}h`;
}

function fmtAbsTime(v) {
  if (!v) return "—";
  try {
    const d = new Date(v);
    if (isNaN(d)) return "—";
    return d.toLocaleString(undefined, {
      month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return "—"; }
}

function SectionAbandonedPlus() {
  const [windowId, setWindowId] = useState("7d");
  const [filterId, setFilterId] = useState("all");
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    let alive = true;
    async function load() {
      setBusy(true);
      try {
        const { data } = await api.get("/admin/plus/abandoned", {
          params: { window: windowId, filter: filterId, limit: 200 },
        });
        if (alive) { setData(data); setErr(null); }
      } catch (e) {
        if (alive) setErr(e?.response?.data?.detail || "Could not load abandoned checkouts.");
      } finally {
        if (alive) setBusy(false);
      }
    }
    load();
    const t = setInterval(load, 60000);
    return () => { alive = false; clearInterval(t); };
  }, [windowId, filterId, reloadTick]);

  async function saveFollowup(userId, updates) {
    try {
      await api.post("/admin/plus/followup", { user_id: userId, ...updates });
      setReloadTick((t) => t + 1);
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not save follow-up.");
    }
  }

  const m = data?.metrics || {};
  const rows = data?.rows || [];

  return (
    <Surface>
      <div className="p-6" data-testid="abandoned-plus-section">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
          <div>
            <h2 className="text-sm font-medium">Abandoned Gully Plus</h2>
            <p className="text-xs text-neutral-500 mt-1">
              Players who opened checkout but didn&apos;t subscribe within 24h.
              Personally reach out — every checkout is a conversation.
            </p>
          </div>
          <div className="flex gap-1 text-xs" data-testid="abandoned-window-toggle">
            {ABANDONED_WINDOWS.map((w) => (
              <button
                key={w.id}
                onClick={() => setWindowId(w.id)}
                className={`px-3 py-1.5 rounded border ${
                  w.id === windowId
                    ? "bg-neutral-800 border-neutral-700 text-neutral-100"
                    : "bg-neutral-950 border-neutral-900 text-neutral-500 hover:text-neutral-300"
                }`}
                data-testid={`abandoned-window-${w.id}`}
              >{w.label}</button>
            ))}
          </div>
        </div>

        {err && (
          <div className="text-red-400 text-sm mb-3" data-testid="abandoned-error">{err}</div>
        )}

        {/* Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-5" data-testid="abandoned-metrics">
          <Metric label="Checkouts opened" value={m.checkouts_started ?? 0}
                  testid="abandoned-metric-checkouts" />
          <Metric label="Subscribed" value={m.subscriptions ?? 0}
                  testid="abandoned-metric-subscribed" />
          <Metric label="Abandoned" value={m.abandoned ?? 0}
                  testid="abandoned-metric-abandoned" />
          <Metric label="Conversion rate"
                  value={`${(m.conversion_rate_pct ?? 0).toFixed(1)}%`}
                  testid="abandoned-metric-conv-rate" />
          <Metric label="Avg time to purchase"
                  value={fmtSecs(m.avg_time_to_purchase_seconds)}
                  testid="abandoned-metric-time-to-purchase" />
          <Metric label="Avg time before abandon"
                  value={fmtSecs(m.avg_time_before_abandon_seconds)}
                  testid="abandoned-metric-time-before-abandon" />
        </div>

        {/* Filter chips */}
        <div className="flex gap-1 text-xs mb-4 flex-wrap" data-testid="abandoned-filter-toggle">
          {ABANDONED_FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setFilterId(f.id)}
              className={`px-3 py-1.5 rounded border ${
                f.id === filterId
                  ? "bg-neutral-800 border-neutral-700 text-neutral-100"
                  : "bg-neutral-950 border-neutral-900 text-neutral-500 hover:text-neutral-300"
              }`}
              data-testid={`abandoned-filter-${f.id}`}
            >{f.label}</button>
          ))}
          {busy && <span className="text-neutral-500 ml-2 self-center">Refreshing…</span>}
        </div>

        {/* Rows */}
        {rows.length === 0 && !busy && (
          <div className="text-sm text-neutral-500 py-4" data-testid="abandoned-empty">
            Nothing to follow up on right now — no abandoned checkouts in this window.
          </div>
        )}

        <div className="space-y-2" data-testid="abandoned-rows">
          {rows.map((r) => (
            <AbandonedRow key={r.user_id} row={r} onSave={saveFollowup} />
          ))}
        </div>
      </div>
    </Surface>
  );
}

function AbandonedRow({ row, onSave }) {
  const [notes, setNotes] = useState(row.followup?.notes || "");
  const [savingNotes, setSavingNotes] = useState(false);
  const contacted = !!row.followup?.contacted_at;
  const high = row.followup?.priority === "high";

  const identifier = row.phone || row.email || row.instagram || row.user_id;
  const waMsg = buildWaMessage(row);
  const igMsg = buildIgMessage(row);

  async function copy(text, label) {
    try {
      await navigator.clipboard.writeText(text || "");
    } catch { /* ignore */ }
    // small transient badge via state is overkill — rely on the alert
    // path only for failures. Successful copies are silent.
  }

  async function saveNotes() {
    if (savingNotes) return;
    setSavingNotes(true);
    try {
      await onSave(row.user_id, { notes });
    } finally {
      setSavingNotes(false);
    }
  }

  return (
    <div
      className={`border rounded p-3 space-y-2 ${
        high ? "border-amber-700/50 bg-amber-950/20" : "border-neutral-800 bg-neutral-950"
      }`}
      data-testid={`abandoned-row-${row.user_id}`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="text-sm space-y-0.5">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-neutral-100" data-testid={`abandoned-row-identifier-${row.user_id}`}>
              {row.phone || "—"}
            </span>
            {row.name && <span className="text-neutral-400">· {row.name}</span>}
            {high && (
              <span className="text-xs text-amber-300 border border-amber-700 rounded-full px-2 py-0.5"
                    data-testid={`abandoned-badge-high-${row.user_id}`}>
                ⭐ High priority
              </span>
            )}
            {contacted && (
              <span className="text-xs text-emerald-300 border border-emerald-700 rounded-full px-2 py-0.5"
                    data-testid={`abandoned-badge-contacted-${row.user_id}`}>
                ✓ Contacted
              </span>
            )}
          </div>
          <div className="text-xs text-neutral-500 flex gap-3 flex-wrap">
            {row.email && <span>📧 {row.email}</span>}
            {row.instagram && <span>📷 {row.instagram}</span>}
            <span className="text-neutral-600 font-mono">{row.user_id}</span>
          </div>
        </div>
        <div className="text-xs text-neutral-500 text-right">
          <div>Opened: <span className="text-neutral-300">{fmtAbsTime(row.checkout_opened_at)}</span></div>
          <div>Last activity: <span className="text-neutral-300">{fmtAbsTime(row.last_activity_at)}</span></div>
          <div>
            Time on checkout:{" "}
            <span className="text-neutral-300">{fmtSecs(row.time_on_checkout_seconds)}</span>
          </div>
          <div>
            Returned later:{" "}
            <span className={row.returned_later ? "text-emerald-400" : "text-neutral-400"}>
              {row.returned_later ? "Yes" : "No"}
            </span>
          </div>
          <div>
            Made it to Stripe:{" "}
            <span className={row.opened_stripe_page ? "text-emerald-400" : "text-neutral-400"}>
              {row.opened_stripe_page ? "Yes" : "No"}
            </span>
          </div>
          <div>Status: <span className="text-neutral-300">{row.plus_status}</span></div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-1.5 text-xs flex-wrap">
        <RowBtn onClick={() => copy(row.phone || "", "phone")}
                testid={`abandoned-copy-phone-${row.user_id}`} disabled={!row.phone}>
          📋 Copy Phone
        </RowBtn>
        <RowBtn onClick={() => copy(waMsg, "wa")}
                testid={`abandoned-copy-wa-${row.user_id}`}>
          📋 Copy WhatsApp Message
        </RowBtn>
        <RowBtn onClick={() => copy(igMsg, "ig")}
                testid={`abandoned-copy-ig-${row.user_id}`}>
          📋 Copy Instagram DM
        </RowBtn>
        <RowBtn onClick={() => window.open(`/admin/legacy?user_id=${encodeURIComponent(row.user_id)}`, "_blank")}
                testid={`abandoned-open-profile-${row.user_id}`}>
          👤 Open Profile
        </RowBtn>
        <RowBtn
          onClick={() => onSave(row.user_id, { contacted: !contacted })}
          testid={`abandoned-toggle-contacted-${row.user_id}`}
          intent={contacted ? "success" : "default"}
        >
          {contacted ? "✓ Contacted" : "Mark as Contacted"}
        </RowBtn>
        <RowBtn
          onClick={() => onSave(row.user_id, { priority: high ? "clear" : "high" })}
          testid={`abandoned-toggle-priority-${row.user_id}`}
          intent={high ? "priority" : "default"}
        >
          {high ? "⭐ High priority" : "Mark as High Priority"}
        </RowBtn>
      </div>

      {/* Notes */}
      <div className="pt-1">
        <label className="text-xs text-neutral-500 mb-1 block">
          Notes {row.followup?.notes_updated_at && (
            <span className="text-neutral-600">
              (updated {fmtAbsTime(row.followup.notes_updated_at)})
            </span>
          )}
        </label>
        <div className="flex gap-1.5">
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder='e.g. "Messaged on WhatsApp", "Waiting until payday", "Wants UPI"'
            className="flex-1 text-xs bg-neutral-950 border border-neutral-800 rounded px-2 py-1.5 text-neutral-200 placeholder-neutral-600 focus:outline-none focus:border-neutral-600"
            data-testid={`abandoned-notes-input-${row.user_id}`}
            maxLength={2000}
          />
          <RowBtn onClick={saveNotes}
                  testid={`abandoned-notes-save-${row.user_id}`}
                  disabled={savingNotes || notes === (row.followup?.notes || "")}>
            {savingNotes ? "Saving…" : "Save"}
          </RowBtn>
        </div>
      </div>
    </div>
  );
}

function RowBtn({ onClick, disabled, children, testid, intent = "default" }) {
  const base = "px-2.5 py-1 rounded border transition-colors";
  const styles = {
    default: "bg-neutral-900 border-neutral-800 text-neutral-300 hover:text-neutral-100 hover:border-neutral-600",
    success: "bg-emerald-950/60 border-emerald-800 text-emerald-200 hover:border-emerald-600",
    priority: "bg-amber-950/60 border-amber-800 text-amber-200 hover:border-amber-600",
  };
  const style = styles[intent] || styles.default;
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`${base} ${style} disabled:opacity-40 disabled:cursor-not-allowed`}
      data-testid={testid}
    >{children}</button>
  );
}

function buildWaMessage(row) {
  const first = (row.name || "").split(" ")[0] || "there";
  return (
`Hey ${first}, it's from Gully 🏏

Saw you were checking out Gully Plus but didn't finish signing up. Totally cool — I just wanted to reach out personally.

Was there something that stopped you? Payment issue, price, or just not the right time? I'd love your honest feedback so I can make Gully better for cricketers like you.

If it was a payment issue, we now support more options in India (UPI coming soon) and other regions.

Reply here whenever works for you. No pressure!

— Coach Gully team`);
}

function buildIgMessage(row) {
  const first = (row.name || "").split(" ")[0] || "there";
  return (
`Hey ${first} 🏏 It's from Gully!

Noticed you started checkout for Gully Plus but didn't finish. No worries at all — just wanted to check in personally.

Anything I can help with? Payment, price, timing? Would love your honest feedback so we can make Gully better.

DM me back whenever 🙌`);
}

// ---------- Iter XX.14 — Cricket Intelligence Graph (v2) ----------
// The Business Impact-sorted view. Every cluster carries a full intel
// block: popularity, growth, pain, return-rate, Plus conversion, trend.
// Sorted by BIS by default — this is the operating system for future
// product prioritization.
const CI_SORT_MODES = [
  { id: "business_impact_score", label: "Business Impact" },
  { id: "player_pain_score",     label: "Pain" },
  { id: "growth_score",          label: "Growth" },
  { id: "plus_conversion_pct",   label: "Plus conv." },
  { id: "return_rate_pct",       label: "Return rate" },
  { id: "source_count",          label: "Volume" },
];

function SectionCricketIntelligence() {
  const [sort, setSort] = useState("business_impact_score");
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rescoring, setRescoring] = useState(false);
  const [expanded, setExpanded] = useState(null); // cluster_id showing related

  useEffect(() => {
    let alive = true;
    async function load() {
      setBusy(true);
      try {
        const { data } = await api.get("/admin/knowledge/intelligence-v2", {
          params: { sort, limit: 25 },
        });
        if (alive) { setData(data); setErr(null); }
      } catch (e) {
        if (alive) setErr(e?.response?.data?.detail || "Could not load Cricket Intelligence.");
      } finally {
        if (alive) setBusy(false);
      }
    }
    load();
    const t = setInterval(load, 90000);
    return () => { alive = false; clearInterval(t); };
  }, [sort]);

  async function rescore() {
    if (rescoring) return;
    setRescoring(true);
    try {
      await api.post("/admin/knowledge/rescore", { all_scores: false });
      const { data } = await api.get("/admin/knowledge/intelligence-v2", {
        params: { sort, limit: 25 },
      });
      setData(data);
    } catch (e) {
      alert(e?.response?.data?.detail || "Rescore failed.");
    } finally {
      setRescoring(false);
    }
  }

  const totals = data?.totals || {};
  const clusters = data?.clusters || [];
  const H = data?.highlights || {};

  return (
    <Surface>
      <div className="p-6" data-testid="cricket-intelligence-section">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
          <div>
            <h2 className="text-sm font-medium">Cricket Intelligence Graph</h2>
            <p className="text-xs text-neutral-500 mt-1">
              Deterministic, zero-LLM. Every meaningful conversation improves
              the graph. Sorted by Business Impact by default.
            </p>
          </div>
          <button
            onClick={rescore}
            disabled={rescoring}
            className="text-xs px-3 py-1.5 rounded border border-emerald-800 bg-emerald-950/40 text-emerald-300 hover:bg-emerald-900/50 disabled:opacity-40"
            data-testid="ci-rescore-btn"
          >
            {rescoring ? "Rescoring…" : "Rescore graph"}
          </button>
        </div>

        {err && <div className="text-red-400 text-sm mb-3" data-testid="ci-error">{err}</div>}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5" data-testid="ci-totals">
          <Metric label="Clusters" value={totals.clusters ?? 0} testid="ci-total-clusters" />
          <Metric label="Graph edges" value={totals.edges ?? 0} testid="ci-total-edges" />
          <Metric label="Highest pain"
                  value={(H.highest_pain?.[0]?.player_pain_score ?? 0).toFixed(0)}
                  testid="ci-highest-pain" />
          <Metric label="Highest Plus %"
                  value={`${(H.highest_plus_conv?.[0]?.plus_conversion_pct ?? 0).toFixed(0)}%`}
                  testid="ci-highest-plus" />
        </div>

        <div className="flex gap-1 text-xs mb-3 flex-wrap" data-testid="ci-sort-toggle">
          {CI_SORT_MODES.map((m) => (
            <button
              key={m.id}
              onClick={() => setSort(m.id)}
              className={`px-3 py-1.5 rounded border ${
                m.id === sort
                  ? "bg-neutral-800 border-neutral-700 text-neutral-100"
                  : "bg-neutral-950 border-neutral-900 text-neutral-500 hover:text-neutral-300"
              }`}
              data-testid={`ci-sort-${m.id}`}
            >{m.label}</button>
          ))}
          {busy && <span className="text-neutral-500 ml-2 self-center">Refreshing…</span>}
        </div>

        {clusters.length === 0 && !busy && (
          <div className="text-sm text-neutral-500 py-4" data-testid="ci-empty">
            No clusters yet. Run the backfill (tools/backfill_knowledge.py) and
            the graph builder (tools/build_knowledge_graph.py).
          </div>
        )}

        <div className="space-y-1.5" data-testid="ci-rows">
          {clusters.map((c) => (
            <ClusterRowV2
              key={c.cluster_id}
              c={c}
              expanded={expanded === c.cluster_id}
              onToggle={() => setExpanded(expanded === c.cluster_id ? null : c.cluster_id)}
            />
          ))}
        </div>

        {/* Declining topics — a signal you want to see */}
        {(H.declining || []).length > 0 && (
          <div className="mt-6">
            <div className="text-xs uppercase tracking-wider text-neutral-500 mb-2">
              Declining topics (BIS-weighted)
            </div>
            <div className="space-y-1" data-testid="ci-declining">
              {H.declining.map((c) => (
                <div key={c.cluster_id}
                     className="text-xs text-neutral-400 border border-neutral-900 bg-neutral-950 rounded p-2 flex justify-between">
                  <span className="truncate">{c.sample_question || c.cluster_key}</span>
                  <span className="text-neutral-500 flex-none ml-3">
                    BIS {c.business_impact_score?.toFixed?.(0) ?? 0} · {c.recent_7d ?? 0}/{c.prev_7d ?? 0}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Surface>
  );
}

function ClusterRowV2({ c, expanded, onToggle }) {
  const [related, setRelated] = useState(null);
  useEffect(() => {
    if (!expanded || related) return;
    api.get(`/admin/knowledge/cluster/${c.cluster_id}/related?limit=5`)
      .then((r) => setRelated(r.data))
      .catch(() => setRelated({ co_occurs: [], follow_up_out: [], follow_up_in: [] }));
  }, [expanded, related, c.cluster_id]);

  const trendPill = c.trend_direction === "rising"
    ? "bg-emerald-950/60 border-emerald-800/60 text-emerald-300"
    : c.trend_direction === "declining"
    ? "bg-red-950/60 border-red-800/60 text-red-300"
    : "bg-neutral-900 border-neutral-800 text-neutral-400";

  return (
    <div
      className="border border-neutral-800 bg-neutral-950 rounded"
      data-testid={`ci-row-${c.cluster_id}`}
    >
      <button
        onClick={onToggle}
        className="w-full text-left p-3 flex items-start justify-between gap-3 hover:bg-neutral-900/50"
      >
        <div className="flex-1 min-w-0">
          <div className="text-xs text-neutral-400 mb-1 flex flex-wrap gap-1.5 items-center">
            <span className="px-1.5 py-0.5 rounded bg-neutral-900 border border-neutral-800 text-neutral-300">
              {c.primary_topic}
            </span>
            {c.question_intent && (
              <span className="px-1.5 py-0.5 rounded bg-neutral-900 border border-neutral-800">
                {c.question_intent}
              </span>
            )}
            {c.role && (
              <span className="px-1.5 py-0.5 rounded bg-emerald-950/60 border border-emerald-900/50 text-emerald-300">
                {c.role}
              </span>
            )}
            {c.trend_direction && (
              <span className={`px-1.5 py-0.5 rounded border text-[10px] ${trendPill}`}>
                {c.trend_direction}
              </span>
            )}
          </div>
          <div className="text-sm text-neutral-200 truncate">
            {c.sample_question || c.cluster_key}
          </div>
          <div className="text-[11px] text-neutral-500 mt-1 flex gap-3 flex-wrap">
            <span>BIS <span className="text-neutral-300 font-medium">{(c.business_impact_score ?? 0).toFixed(1)}</span></span>
            <span>pain {(c.player_pain_score ?? 0).toFixed(0)}</span>
            <span>growth {(c.growth_score ?? 0).toFixed(0)}</span>
            <span>plus% {(c.plus_conversion_pct ?? 0).toFixed(0)}</span>
            <span>ret% {(c.return_rate_pct ?? 0).toFixed(0)}</span>
            <span>vol {c.source_count ?? 0}</span>
            <span>depth {(c.conversation_depth_avg ?? 0).toFixed(1)}</span>
          </div>
        </div>
        <div className="flex-none text-xs text-neutral-500">
          {expanded ? "▲" : "▼"}
        </div>
      </button>
      {expanded && (
        <div className="border-t border-neutral-900 p-3 space-y-2" data-testid={`ci-related-${c.cluster_id}`}>
          {!related && <div className="text-xs text-neutral-500">Loading…</div>}
          {related && (
            <>
              <RelatedList label="Often discussed together" rows={related.co_occurs} />
              <RelatedList label="Common follow-up asks" rows={related.follow_up_out} />
              <RelatedList label="Often follows" rows={related.follow_up_in} />
            </>
          )}
        </div>
      )}
    </div>
  );
}

function RelatedList({ label, rows }) {
  if (!rows || rows.length === 0) return null;
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-neutral-500 mb-1">{label}</div>
      <div className="space-y-1">
        {rows.map((r) => (
          <div key={r.cluster_id}
               className="text-xs text-neutral-300 flex justify-between gap-2">
            <span className="truncate">{r.sample_question || r.cluster_key || r.cluster_id}</span>
            <span className="flex-none text-neutral-500">
              {r.weight}×  · BIS {(r.business_impact_score ?? 0).toFixed(0)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}



// ---------- Iter XX.16 — Gully Pass ----------
// Deterministic, zero-LLM reward system. Founder can generate single-use
// tokens that grant free Gully Plus access. Redemption at /pass/:token.
const PASS_STATUS_FILTERS = [
  { id: "", label: "All" },
  { id: "active", label: "Active" },
  { id: "redeemed", label: "Redeemed" },
  { id: "expired", label: "Expired" },
  { id: "revoked", label: "Revoked" },
];
const PASS_DURATION_PRESETS = [
  { months: 1, label: "1 month" },
  { months: 3, label: "3 months" },
  { months: 6, label: "6 months" },
  { months: 12, label: "12 months" },
];

function SectionGullyPasses() {
  const [analytics, setAnalytics] = useState(null);
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  // Generation form state
  const [genCount, setGenCount] = useState(1);
  const [genDuration, setGenDuration] = useState(1);
  const [genCustom, setGenCustom] = useState(false);
  const [genLabel, setGenLabel] = useState("");
  const [genCampaign, setGenCampaign] = useState("");
  const [generating, setGenerating] = useState(false);
  const [genErr, setGenErr] = useState(null);
  const [lastGenerated, setLastGenerated] = useState([]);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const [aRes, lRes] = await Promise.all([
        api.get("/admin/passes/analytics"),
        api.get("/admin/passes", {
          params: {
            ...(statusFilter ? { status: statusFilter } : {}),
            ...(search ? { search } : {}),
            limit: 200,
          },
        }),
      ]);
      setAnalytics(aRes.data);
      setRows(lRes.data.passes || []);
      setErr(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not load passes.");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search]);

  useEffect(() => { load(); }, [load]);

  async function handleGenerate(e) {
    e.preventDefault();
    setGenErr(null);
    setGenerating(true);
    try {
      const { data } = await api.post("/admin/passes/generate", {
        count: Number(genCount),
        duration_months: Number(genDuration),
        label: genLabel.trim() || null,
        campaign: genCampaign.trim() || null,
      });
      setLastGenerated(data.passes || []);
      // Reset form (keep label/campaign for batch flows)
      setGenCount(1);
      // Refresh analytics + list
      load();
    } catch (e2) {
      setGenErr(e2?.response?.data?.detail || "Generation failed.");
    } finally {
      setGenerating(false);
    }
  }

  async function handleRevoke(token) {
    if (!window.confirm(`Revoke pass ${token}? This cannot be undone.`)) return;
    try {
      await api.post(`/admin/passes/${encodeURIComponent(token)}/revoke`);
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Revoke failed.");
    }
  }

  function passUrl(token) {
    return `${window.location.origin}/pass/${token}`;
  }

  async function copyToClipboard(text) {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Fallback for older browsers — silently ignore.
    }
  }

  return (
    <SectionHeader
      title="Gully Passes"
      subtitle="Single-use links that grant free Gully Plus. Zero Stripe, zero LLM, fully deterministic."
    >
      {/* Analytics header */}
      <div className="surface-1 p-4 mb-4" data-testid="passes-analytics">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Metric label="Total created" value={analytics?.created ?? "—"} testid="passes-total-created" />
          <Metric label="Active" value={analytics?.active ?? "—"} testid="passes-total-active" />
          <Metric label="Redeemed" value={analytics?.redeemed ?? "—"} testid="passes-total-redeemed" />
          <Metric label="Expired" value={analytics?.expired ?? "—"} testid="passes-total-expired" />
          <Metric
            label="Redemption rate"
            value={analytics?.redemption_rate_pct != null ? `${analytics.redemption_rate_pct}%` : "—"}
            testid="passes-redemption-rate"
          />
        </div>
      </div>

      {/* Generate form */}
      <form
        onSubmit={handleGenerate}
        className="surface-1 p-4 mb-4"
        data-testid="passes-generate-form"
      >
        <div className="text-xs uppercase tracking-wide text-neutral-500 mb-3">Create passes</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className="text-[10px] uppercase text-neutral-500 mb-1 block">Duration</label>
            <div className="flex flex-wrap gap-1">
              {PASS_DURATION_PRESETS.map((preset) => (
                <button
                  type="button"
                  key={preset.months}
                  onClick={() => { setGenDuration(preset.months); setGenCustom(false); }}
                  className={`px-2 py-1 text-xs rounded border ${
                    !genCustom && genDuration === preset.months
                      ? "bg-emerald-500 text-neutral-950 border-emerald-500"
                      : "border-neutral-700 text-neutral-300 hover:border-neutral-500"
                  }`}
                  data-testid={`passes-duration-${preset.months}`}
                >
                  {preset.label}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setGenCustom(true)}
                className={`px-2 py-1 text-xs rounded border ${
                  genCustom
                    ? "bg-emerald-500 text-neutral-950 border-emerald-500"
                    : "border-neutral-700 text-neutral-300 hover:border-neutral-500"
                }`}
                data-testid="passes-duration-custom"
              >
                Custom
              </button>
            </div>
            {genCustom && (
              <input
                type="number"
                min="1"
                max="60"
                value={genDuration}
                onChange={(e) => setGenDuration(e.target.value)}
                className="mt-2 w-full bg-neutral-950 border border-neutral-700 rounded px-2 py-1 text-sm"
                placeholder="Months (1–60)"
                data-testid="passes-duration-custom-input"
              />
            )}
          </div>
          <div>
            <label className="text-[10px] uppercase text-neutral-500 mb-1 block">Quantity</label>
            <input
              type="number"
              min="1"
              max="200"
              value={genCount}
              onChange={(e) => setGenCount(e.target.value)}
              className="w-full bg-neutral-950 border border-neutral-700 rounded px-2 py-1 text-sm"
              data-testid="passes-count-input"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase text-neutral-500 mb-1 block">Label (optional)</label>
            <input
              type="text"
              value={genLabel}
              onChange={(e) => setGenLabel(e.target.value)}
              placeholder="e.g. Instagram giveaway"
              className="w-full bg-neutral-950 border border-neutral-700 rounded px-2 py-1 text-sm"
              data-testid="passes-label-input"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase text-neutral-500 mb-1 block">Campaign (optional)</label>
            <input
              type="text"
              value={genCampaign}
              onChange={(e) => setGenCampaign(e.target.value)}
              placeholder="e.g. march_launch"
              className="w-full bg-neutral-950 border border-neutral-700 rounded px-2 py-1 text-sm"
              data-testid="passes-campaign-input"
            />
          </div>
        </div>
        {genErr && (
          <div className="mt-3 text-xs text-red-400" data-testid="passes-generate-error">{genErr}</div>
        )}
        <div className="mt-4 flex items-center gap-3">
          <button
            type="submit"
            disabled={generating}
            className="px-4 py-2 bg-emerald-500 text-neutral-950 text-sm font-medium rounded hover:bg-emerald-400 disabled:opacity-60"
            data-testid="passes-generate-btn"
          >
            {generating ? "Creating…" : "Generate"}
          </button>
        </div>
      </form>

      {/* Just-generated batch preview */}
      {lastGenerated.length > 0 && (
        <div className="surface-1 p-4 mb-4" data-testid="passes-just-generated">
          <div className="text-xs uppercase tracking-wide text-neutral-500 mb-2">
            Just created — copy & share
          </div>
          <div className="space-y-1">
            {lastGenerated.map((p) => (
              <div
                key={p.token}
                className="flex items-center justify-between gap-3 text-xs py-1 border-b border-neutral-800 last:border-0"
              >
                <span className="font-mono text-emerald-400">{passUrl(p.token)}</span>
                <button
                  type="button"
                  onClick={() => copyToClipboard(passUrl(p.token))}
                  className="text-neutral-400 hover:text-neutral-100 flex-none"
                  data-testid={`passes-copy-${p.token}`}
                >
                  Copy link
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters + search */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        {PASS_STATUS_FILTERS.map((f) => (
          <button
            key={f.id || "all"}
            onClick={() => setStatusFilter(f.id)}
            className={`px-2 py-1 text-xs rounded border ${
              statusFilter === f.id
                ? "bg-neutral-100 text-neutral-950 border-neutral-100"
                : "border-neutral-700 text-neutral-300 hover:border-neutral-500"
            }`}
            data-testid={`passes-filter-${f.id || "all"}`}
          >
            {f.label}
          </button>
        ))}
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search token / label / campaign"
          className="ml-auto bg-neutral-950 border border-neutral-700 rounded px-2 py-1 text-xs min-w-[240px]"
          data-testid="passes-search-input"
        />
      </div>

      {/* Passes table */}
      <div className="surface-1 overflow-x-auto" data-testid="passes-table">
        {loading && !rows.length && <div className="p-4 text-xs text-neutral-500">Loading…</div>}
        {err && <div className="p-4 text-xs text-red-400">{err}</div>}
        {!loading && !err && rows.length === 0 && (
          <div className="p-4 text-xs text-neutral-500" data-testid="passes-empty">
            No passes match this filter.
          </div>
        )}
        {rows.length > 0 && (
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-neutral-500 border-b border-neutral-800">
                <th className="p-2 font-normal">Token</th>
                <th className="p-2 font-normal">Status</th>
                <th className="p-2 font-normal">Duration</th>
                <th className="p-2 font-normal">Label</th>
                <th className="p-2 font-normal">Campaign</th>
                <th className="p-2 font-normal">Created</th>
                <th className="p-2 font-normal">Redeemed by</th>
                <th className="p-2 font-normal text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.token} className="border-b border-neutral-900 last:border-0" data-testid={`passes-row-${p.token}`}>
                  <td className="p-2 font-mono text-neutral-100">{p.token}</td>
                  <td className="p-2"><StatusPill status={p.status} /></td>
                  <td className="p-2 text-neutral-300">{p.duration_months} mo</td>
                  <td className="p-2 text-neutral-400 max-w-[160px] truncate">{p.label || "—"}</td>
                  <td className="p-2 text-neutral-400 max-w-[140px] truncate">{p.campaign || "—"}</td>
                  <td className="p-2 text-neutral-500">{shortTime(p.created_at)}</td>
                  <td className="p-2 text-neutral-500 font-mono truncate max-w-[120px]">
                    {p.redeemed_by_user_id || "—"}
                  </td>
                  <td className="p-2 text-right whitespace-nowrap">
                    <button
                      onClick={() => copyToClipboard(passUrl(p.token))}
                      className="text-neutral-400 hover:text-neutral-100 mr-3"
                      data-testid={`passes-copy-link-${p.token}`}
                    >
                      Copy link
                    </button>
                    {p.status === "active" && (
                      <button
                        onClick={() => handleRevoke(p.token)}
                        className="text-red-400 hover:text-red-300"
                        data-testid={`passes-revoke-${p.token}`}
                      >
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </SectionHeader>
  );
}

function StatusPill({ status }) {
  const styles = {
    active: "bg-emerald-950/60 text-emerald-300 border-emerald-900",
    redeemed: "bg-blue-950/60 text-blue-300 border-blue-900",
    expired: "bg-neutral-800 text-neutral-400 border-neutral-700",
    revoked: "bg-red-950/60 text-red-300 border-red-900",
  };
  const cls = styles[status] || "bg-neutral-800 text-neutral-400 border-neutral-700";
  return (
    <span className={`inline-block px-2 py-0.5 rounded border text-[10px] uppercase tracking-wide ${cls}`}>
      {status}
    </span>
  );
}

// ---------- Iter XX.19 — Gully Plus Conversion Intelligence ----------
// Deterministic funnel + drill-down + exit + payment diagnostics + insights.
// Reads /api/admin/plus/conversion/* endpoints. ZERO LLM.
function SectionConversionIntelligence() {
  const [days, setDays] = useState(90);
  const [funnel, setFunnel] = useState(null);
  const [cards, setCards] = useState(null);
  const [insights, setInsights] = useState([]);
  const [diagnostics, setDiagnostics] = useState(null);
  const [exits, setExits] = useState(null);
  const [openStage, setOpenStage] = useState(null);
  const [stageData, setStageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const [f, c, i, d, e] = await Promise.all([
        api.get("/admin/plus/conversion/funnel", { params: { days } }),
        api.get("/admin/plus/conversion/cards", { params: { days: days * 2 } }),
        api.get("/admin/plus/conversion/insights", { params: { days } }),
        api.get("/admin/plus/conversion/payment-diagnostics", { params: { days } }),
        api.get("/admin/plus/conversion/exit-analysis", { params: { days } }),
      ]);
      setFunnel(f.data);
      setCards(c.data);
      setInsights(i.data?.insights || []);
      setDiagnostics(d.data);
      setExits(e.data);
      setErr(null);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Could not load conversion intelligence.");
    } finally { setLoading(false); }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  async function openStageDrilldown(stageId) {
    setOpenStage(stageId);
    setStageData(null);
    try {
      const { data } = await api.get(`/admin/plus/conversion/stage/${encodeURIComponent(stageId)}`,
                                      { params: { days, limit: 100 } });
      setStageData(data);
    } catch (ex) {
      setStageData({ error: ex?.response?.data?.detail || "Drill-down failed." });
    }
  }

  return (
    <SectionHeader
      title="Conversion Intelligence"
      subtitle="Where interested users drop off · deterministic · zero LLM"
    >
      {/* Time-window selector */}
      <div className="flex items-center gap-2 mb-4">
        <span className="text-[10px] uppercase tracking-widest text-neutral-500">Window</span>
        {[30, 90, 180].map((d) => (
          <button
            key={d}
            onClick={() => setDays(d)}
            className={`px-3 py-1 rounded-full text-xs border transition-colors ${
              days === d
                ? "bg-emerald-600/80 border-emerald-500 text-white"
                : "bg-neutral-900 border-neutral-800 text-neutral-300 hover:border-neutral-700"
            }`}
            data-testid={`ci-window-${d}`}
          >
            {d}d
          </button>
        ))}
      </div>

      {loading && <div className="text-neutral-500 text-sm p-4">Loading conversion intelligence…</div>}
      {err && <div className="text-red-400 text-sm p-4">{err}</div>}

      {!loading && !err && (
        <>
          {/* Founder-summary insights strip */}
          <div className="surface-1 p-5 mb-4" data-testid="ci-insights">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-3">
              Top opportunities
            </div>
            {insights.length === 0 ? (
              <div className="text-neutral-500 text-sm">Not enough data to surface insights yet.</div>
            ) : (
              <ul className="space-y-2">
                {insights.map((i, idx) => (
                  <li key={i.id || idx} className="text-sm text-neutral-100 flex gap-2" data-testid={`ci-insight-${idx}`}>
                    <span className="text-emerald-400">▸</span>
                    <span>{i.headline}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Dashboard cards row */}
          <div
            className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 surface-1 p-4"
            data-testid="ci-cards"
          >
            <Metric label="Conversion %" value={fmtPct(cards?.conversion_pct)} testid="ci-conversion-pct" />
            <Metric label="Median days to purchase" value={fmtN(cards?.median_days_to_purchase)} testid="ci-median-dtp" />
            <Metric label="Avg reports (converters)" value={fmtN(cards?.converted?.avg_match_reports)} testid="ci-avg-reports-conv" />
            <Metric label="Avg reports (non-conv.)" value={fmtN(cards?.not_converted?.avg_match_reports)} testid="ci-avg-reports-nonconv" />
            <Metric label="Avg videos (converters)" value={fmtN(cards?.converted?.avg_video_reviews)} testid="ci-avg-videos-conv" />
            <Metric label="Avg convos (converters)" value={fmtN(cards?.converted?.avg_conversations)} testid="ci-avg-convos-conv" />
            <Metric label="Top country (conv.)" value={cards?.converted?.top_country || "—"} testid="ci-top-country" />
            <Metric label="Top device (conv.)" value={cards?.converted?.top_device || "—"} testid="ci-top-device" />
          </div>

          {/* Funnel */}
          <div className="surface-1 p-4 mb-4" data-testid="ci-funnel">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-3">
              Conversion funnel ({funnel?.since_days || days} days)
            </div>
            <div className="space-y-1">
              {(funnel?.stages || []).map((s, idx) => (
                <FunnelRow key={s.id} stage={s} idx={idx} onClick={openStageDrilldown} />
              ))}
            </div>
          </div>

          {/* Payment diagnostics */}
          <div className="surface-1 p-4 mb-4" data-testid="ci-diagnostics">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-3">
              Payment diagnostics · {diagnostics?.total_failed || 0} failed conversions
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {Object.entries(diagnostics?.counts || {}).map(([cls, n]) => (
                <div
                  key={cls}
                  className={`px-3 py-2 rounded border text-xs ${
                    cls === "unknown" && n > 0
                      ? "border-amber-800 bg-amber-950/40 text-amber-200"
                      : "border-neutral-800 bg-neutral-950 text-neutral-300"
                  }`}
                  data-testid={`ci-diagnostic-${cls}`}
                >
                  <div className="text-neutral-500 text-[10px] uppercase mb-1">{cls.replace(/_/g, " ")}</div>
                  <div className="tabular-nums text-neutral-100">
                    {n} <span className="text-neutral-500">({diagnostics?.percents?.[cls] ?? 0}%)</span>
                  </div>
                </div>
              ))}
            </div>
            {(diagnostics?.counts?.unknown || 0) > 0 && (
              <div className="mt-3 text-xs text-amber-300" data-testid="ci-unknown-flag">
                ⚠ {diagnostics.counts.unknown} unknown failures flagged for investigation.
              </div>
            )}
          </div>

          {/* Exit analysis */}
          <ExitAnalysisPanel exits={exits} />
        </>
      )}

      {openStage && (
        <StageDrilldownDrawer
          stageId={openStage}
          data={stageData}
          onClose={() => { setOpenStage(null); setStageData(null); }}
        />
      )}
    </SectionHeader>
  );
}

function fmtPct(v) {
  if (v == null || Number.isNaN(v)) return "—";
  return `${v}%`;
}
function fmtN(v) {
  if (v == null || Number.isNaN(v)) return "—";
  return typeof v === "number" ? (Number.isInteger(v) ? String(v) : v.toFixed(1)) : String(v);
}

function FunnelRow({ stage, idx, onClick }) {
  const available = stage.available !== false;
  const clickable = available;
  const parallel = stage.parallel_path === true;
  return (
    <button
      onClick={() => clickable && onClick(stage.id)}
      disabled={!clickable}
      className={`w-full text-left flex items-center gap-3 px-3 py-2 rounded border transition-colors ${
        !available
          ? "border-neutral-900 bg-neutral-950/60 text-neutral-500 cursor-not-allowed"
          : parallel
          ? "border-amber-900/70 bg-amber-950/20 hover:border-amber-800 hover:bg-amber-950/40"
          : "border-neutral-800 bg-neutral-950 hover:border-neutral-700 hover:bg-neutral-900"
      }`}
      data-testid={`ci-funnel-row-${stage.id}`}
    >
      <div className="w-6 text-[10px] text-neutral-500 tabular-nums">{idx + 1}.</div>
      <div className="flex-1 text-sm">{stage.label}</div>
      <div className="tabular-nums text-sm text-neutral-200 w-16 text-right">
        {available ? (stage.count ?? 0) : "—"}
      </div>
      <div className="tabular-nums text-xs text-neutral-500 w-12 text-right">
        {available && stage.percent_of_top != null ? `${stage.percent_of_top}%` : "—"}
      </div>
      <div className={`tabular-nums text-xs w-16 text-right ${
        stage.drop_off_pct != null && stage.drop_off_pct > 40 ? "text-red-400" :
        stage.drop_off_pct != null && stage.drop_off_pct > 20 ? "text-amber-300" : "text-neutral-500"
      }`}>
        {parallel
          ? <span className="text-amber-400/70 italic">n/a</span>
          : available && stage.drop_off_pct != null
              ? `${stage.drop_off_pct > 0 ? "-" : "+"}${Math.abs(stage.drop_off_pct)}%`
              : "—"}
      </div>
      {!available && (
        <div className="text-[10px] text-neutral-600 italic w-40 truncate" title={stage.reason}>
          {stage.reason}
        </div>
      )}
    </button>
  );
}

function ExitAnalysisPanel({ exits }) {
  const [expanded, setExpanded] = useState(false);
  if (!exits) return null;
  const rows = exits.exits || [];
  return (
    <div className="surface-1 p-4 mb-4" data-testid="ci-exits">
      <button
        onClick={() => setExpanded((v) => !v)}
        className="flex justify-between items-center w-full text-left"
      >
        <div>
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">
            Exit analysis · {rows.length} users viewed Plus but never converted
          </div>
          <div className="text-xs text-neutral-500 mt-1">Click to {expanded ? "collapse" : "expand"}</div>
        </div>
        <span className="text-neutral-500 text-xs">{expanded ? "−" : "+"}</span>
      </button>
      {expanded && (
        <div className="mt-4 space-y-3 max-h-[500px] overflow-y-auto">
          {rows.length === 0 && <div className="text-neutral-500 text-sm">No non-converters in window.</div>}
          {rows.map((e) => (
            <div key={e.user.user_id} className="border border-neutral-800 rounded p-3" data-testid={`ci-exit-${e.user.user_id}`}>
              <div className="flex justify-between items-start gap-3 mb-2">
                <div className="text-sm">
                  <span className="text-neutral-100">{e.user.name || e.user.email_masked || e.user.user_id}</span>
                  {e.user.country && <span className="ml-2 text-xs text-neutral-500">· {e.user.country}</span>}
                </div>
                <div className="text-xs text-neutral-500 tabular-nums">
                  {e.cricket?.match_reports || 0} reports · {e.cricket?.video_reviews_success || 0} videos
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {(e.trail || []).slice().reverse().map((t, i) => (
                  <span
                    key={i}
                    className="px-2 py-0.5 rounded bg-neutral-950 border border-neutral-800 text-[10px] text-neutral-400 font-mono"
                  >
                    {t.kind}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StageDrilldownDrawer({ stageId, data, onClose }) {
  return (
    <div
      className="fixed inset-0 z-40 flex justify-end"
      onClick={onClose}
      data-testid="ci-drilldown"
    >
      <div className="absolute inset-0 bg-black/60" />
      <div
        className="relative w-full sm:max-w-2xl h-full bg-[#0c0c0c] border-l border-neutral-800 overflow-y-auto p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-start mb-4">
          <div>
            <p className="label-eyebrow">Stage drill-down</p>
            <p className="text-sm text-neutral-300 mt-1">{stageId.replace(/_/g, " ")}</p>
            {data?.total != null && (
              <p className="text-xs text-neutral-500 mt-1">
                Showing {data.returned} of {data.total} users
              </p>
            )}
          </div>
          <button onClick={onClose} className="text-neutral-500 hover:text-neutral-100" data-testid="ci-drilldown-close">✕</button>
        </div>
        {!data && <div className="text-neutral-500 text-sm">Loading users…</div>}
        {data?.error && <div className="text-red-400 text-sm">{data.error}</div>}
        {data?.available === false && (
          <div className="text-neutral-500 text-sm italic" data-testid="ci-drilldown-unavailable">
            {data.reason}
          </div>
        )}
        {data?.users?.length > 0 && (
          <div className="space-y-2">
            {data.users.map((u) => (
              <div key={u.user_id} className="border border-neutral-800 rounded p-3 text-xs" data-testid={`ci-drilldown-user-${u.user_id}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-neutral-100 font-medium">{u.name || u.email_masked || u.user_id}</span>
                  {u.plus_status !== "free" && (
                    <span className="px-1.5 py-0.5 rounded bg-emerald-900/50 text-emerald-200 text-[10px] uppercase">{u.plus_status}</span>
                  )}
                  {u.plus_source && (
                    <span className="px-1.5 py-0.5 rounded bg-amber-900/40 text-amber-200 border border-amber-800 text-[10px]">
                      via {u.plus_source}
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-neutral-400">
                  <div>Country: <span className="text-neutral-200">{u.country || "—"}</span></div>
                  <div>Device: <span className="text-neutral-200">{u.device || "—"}</span></div>
                  <div>Signed up: <span className="text-neutral-200">{shortTime(u.created_at)}</span></div>
                  <div>Last convo: <span className="text-neutral-200">{shortTime(u.cricket?.last_conversation_at)}</span></div>
                  <div>Match reports: <span className="text-neutral-200">{u.cricket?.match_reports ?? 0}</span></div>
                  <div>Videos (✓): <span className="text-neutral-200">{u.cricket?.video_reviews_success ?? 0}</span></div>
                  <div>Conversations: <span className="text-neutral-200">{u.cricket?.total_conversations ?? 0}</span></div>
                  <div>Season reviews: <span className="text-neutral-200">{u.cricket?.season_reviews_unlocked ?? 0}</span></div>
                  <div>Journey snapshot: <span className="text-neutral-200">{u.cricket?.journey_snapshot_exists ? "yes" : "no"}</span></div>
                  <div>Confidence data: <span className="text-neutral-200">{u.cricket?.confidence_series_exists ? "yes" : "no"}</span></div>
                </div>
              </div>
            ))}
          </div>
        )}
        {data?.users?.length === 0 && data?.available && (
          <div className="text-neutral-500 text-sm">No users at this stage in window.</div>
        )}
      </div>
    </div>
  );
}

